
import htmlDocx from 'html-docx-js/dist/html-docx';
import html2pdf from 'html2pdf.js';
import moment from "moment";
import API_ROUTES from "../constants/api";
import { ReportPageType, ReportSection } from "../constants/common";
import Utils from "./Utils";
import { normalizeLanguageCode, getTranslation } from "./TranslationHelpers";
import { prepareGhgHtmlForWordExport } from "./ghgWordExportHtml";
import { buildIconGridTocHtml, escapeTocHtml } from "./reportTocWordHtml";
import {
  WORD_DOCX_GHG_COMPAT_INLINE_CSS,
  WORD_DOCX_GHG_COVER_BRSR_TABLE_BORDER_RESET_CSS,
  WORD_DOCX_GHG_COVER_NO_BORDER_CSS,
  WORD_DOCX_GHG_TABLE_P_FULL_WIDTH_CSS,
} from "./wordDocxGhgCompatStyles";
import {
  prepareHtmlForDocxExport,
  WORD_DOCX_GHG_INNER_IMAGE_CSS,
} from "./wordDocxExportPrep";


const BASE_URL_DOMAIN = process.env.REACT_APP_ENV === 'esgx'
  ? `${window.location.protocol}//${window.location.host}/esgx`
  : `${window.location.protocol}//${window.location.host}`;

/** Class added to td only — auto right-align numeric-only body cells in BRSR Word/PDF (headers stay left). */
const BRSR_EXPORT_NUMERIC_CELL_CLASS = 'brsr-export-numeric-cell';
/** On td: skip numeric right-align; force left in Word/PDF (e.g. year of incorporation). */
const BRSR_EXPORT_KEEP_LEFT_CLASS = 'brsr-export-keep-left';
/** Marks td we set align=right on so we do not strip user-supplied alignment */
const BRSR_EXPORT_NUMERIC_ALIGN_ATTR = 'data-brsr-export-numeric-align';

/** Word/html-docx often needs inline text-align; merge without clobbering other declarations */
const mergeCellTextAlignStyle = (el, textAlign) => {
  if (!el?.setAttribute) return;
  let cur = el.getAttribute('style') || '';
  cur = cur.replace(/\btext-align\s*:\s*[^;]+;?/gi, '').replace(/;\s*;/g, ';').trim();
  const addition = `text-align: ${textAlign}`;
  const next = cur ? (cur.endsWith(';') ? `${cur} ${addition}` : `${cur}; ${addition}`) : addition;
  el.setAttribute('style', next);
};

/** Direct child p/div only (avoid :scope — not supported in jsdom/nwsapi). */
const mergeTextAlignOnDirectChildBlocks = (cell, textAlign) => {
  [...cell.children].forEach((ch) => {
    const tag = String(ch.tagName).toLowerCase();
    if (tag === 'p' || tag === 'div') {
      if (!ch.querySelector?.('table')) mergeCellTextAlignStyle(ch, textAlign);
    }
  });
};

const applyBrsrExportNumericTdMarkup = (cell, isNumeric) => {
  if (isNumeric) {
    cell.classList.add(BRSR_EXPORT_NUMERIC_CELL_CLASS);
    cell.setAttribute('align', 'right');
    cell.setAttribute(BRSR_EXPORT_NUMERIC_ALIGN_ATTR, '1');
    mergeCellTextAlignStyle(cell, 'right');
    mergeTextAlignOnDirectChildBlocks(cell, 'right');
  } else {
    cell.classList.remove(BRSR_EXPORT_NUMERIC_CELL_CLASS);
    if (cell.getAttribute(BRSR_EXPORT_NUMERIC_ALIGN_ATTR) === '1') {
      cell.removeAttribute('align');
      cell.removeAttribute(BRSR_EXPORT_NUMERIC_ALIGN_ATTR);
    }
  }
};

/** BRSR Section A detail rows: answer td should stay left even when value is numeric (editor may strip class). */
const SECTION_A_FORCE_LEFT_LABEL_RES = [
  /year\s+of\s+incorporation/i,
  /paid[-\s]?up\s+capital/i,
];

export const tdNeedsBrsrExportForceLeft = (cell) => {
  if (!cell || String(cell.tagName).toLowerCase() !== 'td') return false;
  if (cell.classList.contains(BRSR_EXPORT_KEEP_LEFT_CLASS)) return true;
  const tr = cell.closest('tr');
  if (!tr) return false;
  const kids = [...tr.children].filter((n) => n.nodeType === 1);
  const idx = kids.indexOf(cell);
  if (idx !== 2) return false;
  if (String(kids[0]?.tagName).toLowerCase() !== 'th') return false;
  if (String(kids[1]?.tagName).toLowerCase() !== 'th') return false;
  if (String(kids[2]?.tagName).toLowerCase() !== 'td') return false;
  const label = (kids[1].textContent || '').replace(/\s+/g, ' ').trim();
  return SECTION_A_FORCE_LEFT_LABEL_RES.some((re) => re.test(label));
};

const applyBrsrExportTdForceLeft = (cell) => {
  applyBrsrExportNumericTdMarkup(cell, false);
  cell.setAttribute('align', 'left');
  mergeCellTextAlignStyle(cell, 'left');
  mergeTextAlignOnDirectChildBlocks(cell, 'left');
};

/**
 * Shared table + section spacing for BRSR Word (html-docx) and print/PDF HTML.
 * Fixes double borders (row + cell), cramped padding, and uneven header alignment in exports.
 */
const BRSR_EXPORT_TABLE_AND_SPACING_CSS = `
          table,
          table * {
            box-sizing: border-box;
          }
          table,
          table.table,
          table.table-bordered {
            width: 100%;
            max-width: 100%;
            margin-bottom: 12px;
            border-collapse: collapse;
            border-spacing: 0;
            border: 1px solid #dee2e6;
            table-layout: auto !important;
            text-align: left;
          }
          table col,
          table colgroup col {
            width: auto !important;
          }
          tr {
            border: none;
          }
          th,
          td {
            border: 1px solid #dee2e6;
            border-width: 1px;
            padding: 7px 10px;
            vertical-align: top;
            width: auto;
          }
          .table > :not(caption) > * > * {
            padding: 7px 10px;
            border: 1px solid #dee2e6;
            box-shadow: none;
          }
          .table-bordered > :not(caption) > * > *,
          table.table-bordered > :not(caption) > * > * {
            border: 1px solid #dee2e6 !important;
            border-width: 1px !important;
            box-shadow: none !important;
          }
          .table-sm > :not(caption) > * > * {
            padding: 6px 8px;
          }
          .text-end,
          .text-right,
          td.text-end,
          td.text-right,
          tbody td.text-end,
          tbody td.text-right,
          .table tbody td.text-end,
          .table tbody td.text-right,
          table.table-bordered td.text-end,
          table.table-bordered td.text-right,
          .ETA_card_Section_sub_body .table td.text-end,
          .ETA_card_Section_sub_body .table td.text-right,
          .ETA_card_Section_sub_body .table .text-end,
          .ETA_card_Section_sub_body .table .text-right {
            text-align: right !important;
          }
          td.${BRSR_EXPORT_NUMERIC_CELL_CLASS} {
            text-align: right !important;
          }
          td.${BRSR_EXPORT_KEEP_LEFT_CLASS} {
            text-align: left !important;
          }
          th,
          th.text-end,
          th.text-right,
          thead th,
          thead th.text-end,
          thead th.text-right,
          .table thead th,
          .table thead th.text-end,
          .table thead th.text-right,
          table.table-bordered th,
          table.table-bordered th.text-end,
          table.table-bordered th.text-right,
          .ETA_card_Section_sub_body .table th,
          .ETA_card_Section_sub_body .table th.text-end,
          .ETA_card_Section_sub_body .table th.text-right {
            text-align: left !important;
          }
          .Main_Section {
            margin-bottom: 10px;
          }
          .ETA_card_Section_sub {
            margin-bottom: 14px;
          }
          .ETA_card_Section_head {
            margin-top: 18px;
            margin-bottom: 10px;
          }
          .ETA_card_Section > .ETA_card_Section_head:first-child {
            margin-top: 0;
          }
`;

/** Word/print HTML from saved markup may use React-style className=; DOCX and print need class=. */
const normalizeHtmlClassAndPageBreaksForExport = (html) =>
  html
    .replace(/\bclassName=/g, 'class=')
    .replace(/br clear="all"/g, 'br clear="all" style="page-break-before:always"')
    .replace(/br clear='all'/g, `br clear='all' style="page-break-before:always"`);

/**
 * Whether table cell text is only a numeric value (with typical BRSR formatting).
 * Used to right-align figures in Word/PDF without affecting label columns.
 */
export const isBrsrExportNumericCellText = (raw) => {
  if (raw == null) return false;
  let s = String(raw).replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return false;
  if (/^(NA|N\/A|NIL|—|–|-)$/i.test(s)) return false;

  s = s
    .replace(/^(\u20B9|₹|Rs\.?|INR|USD|\$)\s*/i, '')
    .replace(/\s*(\u20B9|₹|Rs\.?|INR|USD|\$)$/i, '')
    .trim();

  if (s.endsWith('%')) {
    s = s.slice(0, -1).trim();
  }

  if (/^\(.*\)$/.test(s)) {
    s = s.slice(1, -1).trim();
  }

  s = s.replace(/^[−–\-]/, '').trim();

  if (!/^[\d,\.]+$/.test(s)) return false;
  if (!/\d/.test(s)) return false;
  const dotCount = (s.match(/\./g) || []).length;
  if (dotCount > 1) return false;
  if (s.startsWith(',') || s.endsWith(',')) return false;
  return true;
};

/**
 * Adds {@link BRSR_EXPORT_NUMERIC_CELL_CLASS} to td only when text is numeric-only (see {@link isBrsrExportNumericCellText}).
 * All th lose the class (headers stay left-aligned). Skips td that contain nested tables.
 */
export const markBrsrExportNumericTableCells = (htmlFragment) => {
  if (!htmlFragment || typeof htmlFragment !== 'string') return htmlFragment;
  if (typeof DOMParser === 'undefined') return htmlFragment;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div id="brsr-export-root">${htmlFragment}</div>`,
      'text/html',
    );
    const root = doc.getElementById('brsr-export-root');
    if (!root) return htmlFragment;
    root.querySelectorAll('table th').forEach((cell) => {
      cell.classList.remove(BRSR_EXPORT_NUMERIC_CELL_CLASS);
      cell.setAttribute('align', 'left');
      mergeCellTextAlignStyle(cell, 'left');
    });
    root.querySelectorAll('table td').forEach((cell) => {
      if (cell.querySelector('table')) {
        applyBrsrExportNumericTdMarkup(cell, false);
        return;
      }
      if (tdNeedsBrsrExportForceLeft(cell)) {
        applyBrsrExportTdForceLeft(cell);
        return;
      }
      applyBrsrExportNumericTdMarkup(
        cell,
        isBrsrExportNumericCellText(cell.textContent),
      );
    });
    return root.innerHTML;
  } catch (e) {
    return htmlFragment;
  }
};

/**
 * Right-align numeric-only td under root; strips numeric class from all th. Skips td that contain nested tables.
 */
export const syncBrsrNumericCellAlignment = (rootElement) => {
  if (!rootElement || typeof rootElement.querySelectorAll !== 'function') return;
  rootElement.querySelectorAll('table th').forEach((cell) => {
    cell.classList.remove(BRSR_EXPORT_NUMERIC_CELL_CLASS);
    cell.setAttribute('align', 'left');
    mergeCellTextAlignStyle(cell, 'left');
  });
  rootElement.querySelectorAll('table td').forEach((cell) => {
    if (cell.querySelector('table')) {
      applyBrsrExportNumericTdMarkup(cell, false);
      return;
    }
    if (tdNeedsBrsrExportForceLeft(cell)) {
      applyBrsrExportTdForceLeft(cell);
      return;
    }
    applyBrsrExportNumericTdMarkup(
      cell,
      isBrsrExportNumericCellText(cell.textContent),
    );
  });
};

const qAnserData = (answer) => {
  return !!answer.uom ? answer.uom == "no." ? parseInt(answer.value) : answer.value + ' ' + answer.uom : answer.value
}

const pageHeaderFunction = (templatedata, lang, type) => {
  const findLangValue = templatedata.localization.find(data => data.languageCode == lang)
  if (!!findLangValue) {
    if (type == 'Social') {
      return (`<h4><span style="font-size: 24pt; color: #007bff;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;">`)
    } else if (type == 'Governance') {
      return (`<h4><span style="font-size: 24pt; color: #f7c300;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;">`)
    } else {
      return (`<h4><span style="font-size: 24pt; color: rgb(45, 194, 107);"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;">`)
    }
  } else {
    return ''
  }
}

const pageSectionFunction = (templatedata, lang) => {
  const findLangValue = templatedata.localization.find(data => data.languageCode == lang)
  if (!!findLangValue) {
    return (`<h5 style='font-size:1.25rem;margin:0px 0px 8px 0px;'>${findLangValue.text}</h5>`)
  } else {
    return ''
  }
}

const pageSubSectionFunction = (templatedata, lang, type) => {
  const findLangValue = templatedata.localization.find(data => data.languageCode == lang)
  if (!!findLangValue) {
    return (`<h6 style='font-size: 16px;margin:0px 0px 8px 0px;'>${findLangValue.text}</h6>`)
  } else {
    return ''
  }
}

const pageQuestionFunction = (templatedata, lang) => {
  const findLangValue = templatedata.localization.find(val => val.languageCode == lang)
  if (!!findLangValue) {
    return (
      `<div style="margin-bottom: 20px;font-size:16px;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>
    <div style="margin-bottom: 20px;font-size:16px;text-align: justify;"><span style="color: rgb(0, 0, 0);">${!!templatedata.keyValues && templatedata.keyValues.length > 0 ? qAnserData(templatedata.keyValues[0]) : ""}</span></div>`
    )
  } else {
    return ""
  }
}

const pagetableTYPE_1Function = (templatedata, lang) => {
  var contentHtml = ''
  const findLangValue = templatedata.localization.find(val => val.languageCode == lang)
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:16px;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin: 10px 0px;">
    <table style='width:100%;font-size:16px;border-collapse: collapse; border-spacing: 0;'>
      <thead><tr style="background-color: rgb(53, 152, 219); height: 20px;">`

  const filterTopHeader = templatedata.metadata.filter(val => val.columnPosition == 'TOP')
  filterTopHeader.forEach(item => {
    const findLangValue = item.localization.find(val => val.languageCode == lang)
    if (!!findLangValue) {
      contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; width: 50%; height: 20px;"><span style="color: rgb(236, 240, 241);">${findLangValue.text}</span></th>`
    }
  })

  contentHtml += `</tr></thead><tbody>`
  const filterLeftHeader = templatedata.metadata.filter(val => val.columnPosition == 'LEFT')
  if (!!filterLeftHeader && filterLeftHeader.length > 0) {
    filterLeftHeader.forEach(item => {
      const findLangValue = item.localization.find(val => val.languageCode == lang)
      const findAnsValue = templatedata.keyValues.find(val => val.key == item.columnCode)
      if (!!findLangValue) {
        contentHtml += `<tr style="height: 20px;"><td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:16px">${findLangValue.text}</td>
                        <td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:16px"><span style="color: rgb(0, 0, 0);">${!!findAnsValue ? qAnserData(findAnsValue) : ""}</span></td></tr>`
      }
    })
  } else {
    contentHtml += `</tr></thead><tbody><tr style="height: 20px;">`
    templatedata.keyValues.forEach(item => {
      if (!!findLangValue) {
        const element1 = item
        contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:16px"><span style="color: rgb(0, 0, 0);">${qAnserData(element1)}</span></td>`
      }
    })
    contentHtml += `</tr>`
  }
  contentHtml += `</tbody></table></div>`
  return contentHtml
}


const pagetableTYPE_2Function = (templatedata, lang) => {
  var contentHtml = ''
  const findLangValue = templatedata.localization.find(val => val.languageCode == lang)
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:16px;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  // contentHtml += `<div style="margin: 10px 0px;">
  //   <table style='width:100%;font-size:16px;border-collapse: collapse; border-spacing: 0;'>
  //     <thead><tr style="background-color: rgb(53, 152, 219); height: 20px;">
  //     <th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; width: 80%; height: 20px;"><span style="color: rgb(236, 240, 241);">Item</span></th>
  //     <th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; width: 20%; height: 20px;"><span style="color: rgb(236, 240, 241);">Value</span></th>
  //     </tr></thead>`
  contentHtml += `<div style="margin: 10px 0px;">
  <table style='width:100%;font-size:16px;border-collapse: collapse; border-spacing: 0;'><tbody>`
  for (let i = 0; i < templatedata.metadata.length; i++) {
    const element = templatedata.metadata[i];
    const findLangValue = element.localization.find(val => val.languageCode == lang)
    if (!!findLangValue) {
      contentHtml += `<tr style="height: 20px;">
      <td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:16px">${findLangValue.text}</td>
      <td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:16px"><span style="color: rgb(0, 0, 0);">${!!templatedata.keyValues[i] ? qAnserData(templatedata.keyValues[i]) : ""}</span></td>
    </tr>`
    }
  }
  contentHtml += `</tbody></table></div>`
  return contentHtml
}


const pageHtml = (templateData, lang, type) => {
  if (!!templateData && templateData.length > 0) {
    var htmlContent = ''
    for (let index = 0; index < templateData.length; index++) {
      const element = templateData[index];
      if (element.keyValueType == "PAGE") {
        htmlContent += pageHeaderFunction(element, lang, type)
      }
      if (element.keyValueType == "SECTION") {
        htmlContent += pageSectionFunction(element, lang)
      }
      if (element.keyValueType == "SUB_SECTION") {
        htmlContent += pageSubSectionFunction(element, lang, type)
      }
      if (element.keyValueType == "SINGLE_VALUE") {
        htmlContent += pageQuestionFunction(element, lang)
      }
      if (element.keyValueType == "TYPE_1_TABLE") {
        htmlContent += pagetableTYPE_1Function(element, lang)
      }
      if (element.keyValueType == "TYPE_2_TABLE") {
        htmlContent += pagetableTYPE_2Function(element, lang)
      }
    }
    htmlContent += `</div>`
    return htmlContent
  } else {
    return `<div></div>`
  }

}

export default class ReportingUtils {

  static getMetricDataPageDefaultHTML() {
    return `
      <div style="font-family: Arial, sans-serif;font-weight:400;">
        <div>
          <h4>GRI Content Index</h4>
          <p>&nbsp;</p>
        </div>
      </div>
    `;
  }

  static isGriTocSkippedSection(section) {
    const skipTypes = new Set([
      ReportSection.TITLE,
      ReportSection.CONTENTS,
      ReportSection.ROOT,
      'TITLE',
      'CONTENTS',
      'ROOT',
    ]);
    return skipTypes.has(section?.type);
  }

  static buildGriTocPageNumberMap(report) {
    const pageMap = new Map();
    let pageNum = 1;
    const sections = [...(report?.sections || [])].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
    );

    sections.forEach((section) => {
      const pages = [...(section.pages || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
      );
      pages.forEach((page) => {
        pageMap.set(`${section.index}-${page.index}`, pageNum);
        pageNum += 1;
      });
    });

    return pageMap;
  }

  static buildGriTocLeaderRow({
    label,
    pageNum,
    rowClass,
    dataAttrs = '',
    labelStyle,
  }) {
    const labelEscaped = escapeTocHtml(label);
    const pageNumStr = pageNum != null ? String(pageNum) : '';
    const leaderCellStyle = 'border-bottom: 1px dotted #94a3b8; vertical-align: bottom; width: 100%; padding: 0 6px; font-size: 1pt; line-height: 1; color: transparent;';
    const pageCellStyle = 'white-space: nowrap; vertical-align: bottom; text-align: right; padding: 6px 0 6px 12px; font-size: 11pt; color: #1e293b;';

    return `<tr class="${rowClass}" ${dataAttrs}>
      <td style="${labelStyle}">${labelEscaped}</td>
      <td style="${leaderCellStyle}">.</td>
      <td style="${pageCellStyle}">${pageNumStr}</td>
    </tr>`;
  }

  /**
   * Build GRI Table of Contents HTML from the current report sections/pages (matches sidebar).
   */
  static buildGriTableOfContentsHtml(report, title = 'Table of Contents') {
    const sections = [...(report?.sections || [])].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
    );
    const pageMap = ReportingUtils.buildGriTocPageNumberMap(report);
    const titleEscaped = escapeTocHtml(title);

    const sectionLabelStyle = 'white-space: nowrap; vertical-align: bottom; padding: 10px 8px 6px 0; font-size: 11pt; font-weight: 600; color: #1e293b;';
    const pageLabelStyle = 'white-space: nowrap; vertical-align: bottom; padding: 5px 8px 5px 28px; font-size: 11pt; font-weight: 400; color: #1d4ed8;';

    let rows = '';

    sections.forEach((section) => {
      if (ReportingUtils.isGriTocSkippedSection(section)) return;

      const pages = [...(section.pages || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
      );
      if (!pages.length) return;

      const sectionName = section.name || '';
      const sectionNameAttr = escapeTocHtml(sectionName);
      const firstPageNum = pageMap.get(`${section.index}-${pages[0].index}`);

      rows += ReportingUtils.buildGriTocLeaderRow({
        label: sectionName,
        pageNum: firstPageNum,
        rowClass: 'gri-toc-section-row',
        dataAttrs: `data-section-name="${sectionNameAttr}"`,
        labelStyle: sectionLabelStyle,
      });

      pages.forEach((page) => {
        const pageNum = pageMap.get(`${section.index}-${page.index}`);
        const pageName = page.name || '';
        const pageNameAttr = escapeTocHtml(pageName);

        rows += ReportingUtils.buildGriTocLeaderRow({
          label: pageName,
          pageNum,
          rowClass: 'gri-toc-page-link',
          dataAttrs: `data-section-index="${section.index}" data-page-index="${page.index}" data-section-name="${sectionNameAttr}" data-page-name="${pageNameAttr}"`,
          labelStyle: pageLabelStyle,
        });
      });
    });

    return `<div class="gri-toc-traditional" style="font-family: 'Inter', Arial, Helvetica, sans-serif; color: #1e293b; padding: 40px 50px 48px;">
      <h2 class="gri-toc-traditional__title" style="font-size: 22pt; font-weight: 700; margin: 0 0 28px 0; color: #0f172a; font-family: Georgia, 'Times New Roman', serif;">${titleEscaped}</h2>
      <table class="gri-toc-traditional__table" cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%; border-collapse: collapse; table-layout: fixed;">
        <colgroup>
          <col style="width: 42%;" />
          <col style="width: auto;" />
          <col style="width: 48px;" />
        </colgroup>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  }

  static applyGriTableOfContentsToReport(report, title = 'Table of Contents') {
    if (!report?.sections?.length) return report;

    const html = ReportingUtils.buildGriTableOfContentsHtml(report, title);
    const contentsSection = report.sections.find(
      (section) => section.type === ReportSection.CONTENTS || section.type === 'CONTENTS',
    );
    const tocPage = contentsSection?.pages?.find(
      (page) => page.type === ReportPageType.CONTENTS || page.type === 'CONTENTS',
    ) || contentsSection?.pages?.[0];
    if (tocPage) {
      tocPage.html = html;
    }
    return report;
  }

  /** Ensure GRI Contents section uses the parent section label, not the TOC page title. */
  static normalizeGriContentsSectionName(report, sectionName = 'Contents') {
    const contentsSection = report?.sections?.find(
      (section) => section.type === ReportSection.CONTENTS || section.type === 'CONTENTS',
    );
    if (contentsSection) {
      contentsSection.name = sectionName;
    }
    return report;
  }

  /**
   * Strip leading numbering from TOC headings (e.g. "5. Environment" -> "Environment").
   */
  static normalizeTocHeadingText(text) {
    return text?.trim().replace(/^\d+\.\s*/, '') || '';
  }

  static isTocSectionHeaderElement(element) {
    if (!element) return false;
    if (element.classList?.contains('gri-toc-section-row')) return true;
    const tag = element.tagName?.toLowerCase();
    const style = (element.getAttribute('style') || '').toLowerCase();
    if (tag === 'li') {
      return style.includes('font-weight:bold') || style.includes('font-weight: bold');
    }
    if (tag === 'div') {
      return style.includes('font-weight: 600') || style.includes('font-weight:600')
        || style.includes('font-weight: bold') || style.includes('font-weight:bold');
    }
    return false;
  }

  /**
   * Walk the TOC DOM upward from a page row to find its section heading text.
   */
  static findTocSectionHeadingFromPageLi(pageEl) {
    if (!pageEl) return null;

    const pageRow = pageEl.closest?.('tr.gri-toc-page-link');
    if (pageRow) {
      if (pageRow.dataset.sectionName) {
        return ReportingUtils.normalizeTocHeadingText(pageRow.dataset.sectionName);
      }

      let prev = pageRow.previousElementSibling;
      while (prev) {
        if (prev.classList?.contains('gri-toc-section-row')) {
          return ReportingUtils.normalizeTocHeadingText(
            prev.dataset.sectionName || prev.querySelector('td')?.textContent,
          );
        }
        prev = prev.previousElementSibling;
      }
    }

    const innerUl = pageEl.closest?.('ul');
    if (!innerUl) return null;

    const outerUl = innerUl.parentElement;
    if (outerUl?.tagName === 'UL' && outerUl !== innerUl) {
      const headerLi = Array.from(outerUl.children).find(
        (child) => child.tagName === 'LI' && ReportingUtils.isTocSectionHeaderElement(child),
      );
      if (headerLi) {
        return ReportingUtils.normalizeTocHeadingText(headerLi.textContent);
      }
    }

    let sibling = innerUl.previousElementSibling;
    while (sibling) {
      if (ReportingUtils.isTocSectionHeaderElement(sibling)) {
        return ReportingUtils.normalizeTocHeadingText(sibling.textContent);
      }
      sibling = sibling.previousElementSibling;
    }

    const groupContainer = innerUl.parentElement;
    if (groupContainer) {
      const headerDiv = groupContainer.querySelector(':scope > div[style*="font-weight"]');
      if (headerDiv && ReportingUtils.isTocSectionHeaderElement(headerDiv)) {
        return ReportingUtils.normalizeTocHeadingText(headerDiv.textContent);
      }
    }

    return null;
  }

  static findGriSectionByTocHeading(sections, headingText) {
    if (!headingText || !sections?.length) return null;

    const normalizedHeading = ReportingUtils.normalizeTocHeadingText(headingText);
    const skipSectionTypes = new Set([0, 1, 2, 'ROOT', 'TITLE', 'CONTENTS']);

    return sections.find((section) => {
      if (skipSectionTypes.has(section.type)) return false;
      const sectionName = section.name?.trim() || '';
      return sectionName === normalizedHeading || sectionName === headingText.trim();
    }) || null;
  }

  static findGriPageInSection(section, pageName) {
    if (!section || !pageName) return null;
    const normalizedPageName = pageName.trim();
    return section.pages?.find((page) => page.name?.trim() === normalizedPageName) || null;
  }

  /**
   * Resolve a TOC click target to section/page indices for GRI report navigation.
   * Uses data attributes when present, then section heading + page name together.
   */
  static resolveGriTocPageNavigation(target, sections) {
    if (!target || !sections?.length) return null;

    const link = target.closest?.('[data-section-index][data-page-index]');
    if (!link) return null;

    if (link.classList?.contains('gri-toc-section-row')
      || ReportingUtils.isTocSectionHeaderElement(link)) {
      return null;
    }

    const pageName = link.dataset.pageName?.trim()
      || link.querySelector('td')?.textContent?.trim();
    if (!pageName) return null;

    const sIndex = parseInt(link.dataset.sectionIndex, 10);
    const pIndex = parseInt(link.dataset.pageIndex, 10);
    if (!Number.isNaN(sIndex) && !Number.isNaN(pIndex)) {
      const section = sections.find((s) => s.index === sIndex);
      const page = section?.pages?.find((p) => p.index === pIndex);
      if (page && page.name?.trim() === pageName) {
        return { sIndex, pIndex };
      }
    }

    const dataSectionName = link.dataset.sectionName?.trim();
    if (dataSectionName) {
      const section = ReportingUtils.findGriSectionByTocHeading(sections, dataSectionName);
      const page = ReportingUtils.findGriPageInSection(section, pageName);
      if (section && page) {
        return { sIndex: section.index, pIndex: page.index };
      }
    }

    const sectionHeading = ReportingUtils.findTocSectionHeadingFromPageLi(link);
    if (sectionHeading) {
      const section = ReportingUtils.findGriSectionByTocHeading(sections, sectionHeading);
      const page = ReportingUtils.findGriPageInSection(section, pageName);
      if (section && page) {
        return { sIndex: section.index, pIndex: page.index };
      }
    }

    return null;
  }


  static getCoverSection(reportClone, companyId, t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
      "<companyId>",
      companyId
    );
    return {
      name: getTranslation(i18n, "FrontCoverpage.label", language),
      index: 0,
      sortOrder: 1,
      type: ReportSection.TITLE,
      pages: [{
        name: getTranslation(i18n, "FrontCoverpage.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.TITLE,
        html: `
          <div style="display: block; text-align: center;height: 100%; font-family: Arial, sans-serif;font-weight:400;">
            <div style="margin-bottom:1rem;"><img src="${logoURL}"/></div>
            <h4 style="text-align: center; font-size: 16px;">${reportClone.name}</h4>
          </div>
        `,
      }]
    }
  }

  static getTableOfContentsSection(t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "Contents.label", language),
      index: 1,
      sortOrder: 2,
      type: ReportSection.CONTENTS,
      pages: [{
        name: getTranslation(i18n, "TableofContents.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.CONTENTS,
        html: `
        <div style="font-family: Arial, sans-serif;">
            <div style="border-left: 4px solid rgb(45, 194, 107); padding-left: 12px; margin-bottom: 38px;">
              <h4 style="font-size: 16pt; margin: 4px 0 0; color: #111827;">
                ${getTranslation(i18n, "TableofContents.label", language)}
              </h4>
            </div>

            <table style="border-collapse: collapse; width: 100%; border-width: 0px; font-size: 15px;">
              <colgroup>
                <col style="width: 46%;">
                <col style="width: 8%;">
                <col style="width: 46%;">
              </colgroup>
              <tbody>
                <tr>
                  <td style="border-width: 0px; padding-right: 16px; vertical-align: top;">
                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        1. ${getTranslation(i18n, "Leadershipmessages.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "LetterfromCEOChairmanMD.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "MessagefromtheChiefSustainabilityOfficer.label", language)}</li>
                      </ul>
                    </div>

                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        2. ${getTranslation(i18n, "AbouttheReport.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "ESGStandardsFrameworks.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "ESGHighlights.label", language)}</li>
                      </ul>
                    </div>

                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        3. ${getTranslation(i18n, "AbouttheCompany.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "OurPurpose.label", language)}</li>
                      </ul>
                    </div>

                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        4. ${getTranslation(i18n, "OurESGApproach.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "KeyStakeholderIssues.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "ESGGoals.label", language)}</li>
                      </ul>
                    </div>

                    <div>
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        5. ${getTranslation(i18n, "Environment.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Overview.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Energy.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "CarbonEmissions.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Waste.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Water.label", language)}</li>
                      </ul>
                    </div>
                  </td>

                  <td style="border-width: 0px;"></td>

                  <td style="border-width: 0px; padding-left: 16px; vertical-align: top;">
                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        6. ${getTranslation(i18n, "Social.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Overview.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Customers.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Community.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Employees.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Suppliers.label", language)}</li>
                      </ul>
                    </div>

                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        7. ${getTranslation(i18n, "Governance.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "Overview.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "ESGGovernance.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "BoardGovernance.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "HumanRights.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "EthicsCompliance.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "TaxPolicy.label", language)}</li>
                        <li style="padding: 2px 0;">${getTranslation(i18n, "BusinessContinuityDisasterRecovery.label", language)}</li>
                      </ul>
                    </div>

                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        8. ${getTranslation(i18n, "ESGDisclosureIndex.label", language)}
                      </div>
                      <ul style="list-style: none; margin: 4px 0 0; padding-left: 12px;">
                        <li style="padding: 2px 0;">${getTranslation(i18n, "GRIIndex.label", language)}</li>
                      </ul>
                    </div>

                    <div>
                      <div style="font-weight: 600; padding: 4px 8px; border-radius: 6px; background-color: #f1f5f9; color: #111827;">
                        9. ${getTranslation(i18n, "BackCover.label", language)}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
        </div>
        `,
      }]
    };
  }

  static getLeadershipSection(t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "Leadershipmessages.label", language),
      index: 2,
      sortOrder: 3,
      type: ReportSection.LEADERSHIP,
      pages: [
        {
          name: getTranslation(i18n, "LetterfromCEOChairmanMD.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.OTHERS,
          html: `
          <div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
            <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Leadershipmessages.label", language)}</span></strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
            
            <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "LetterfromCEOChairmanMD.label", language)}</strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
            <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>

          </div>`,
        },
        {
          name: getTranslation(i18n, "MessagefromtheChiefSustainabilityOfficer.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.OTHERS,
          html: `
          <div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
            <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Leadershipmessages.label", language)}</span></strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
            
            <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "MessagefromtheChiefSustainabilityOfficer.label", language)}</strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
            <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>

          </div>`,
        },

      ]
    }
  }

  static getAboutReportSection(t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "AbouttheReport.label", language),
      index: 3,
      sortOrder: 4,
      type: ReportSection.ABOUT_REPORT,
      pages: [
        {
          name: getTranslation(i18n, "ESGStandardsFrameworks.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.ABOUT_REPORT,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "AbouttheReport.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "ESGStandardsFrameworks.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image3.webp" alt="" width="100%" height="200"></p>
          <p style="font-size: 16px;"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "ESGHighlights.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.ESG_HIGHLIGHTS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "AbouttheReport.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>

          <h4 style="text-align: left;"><strong><span style="font-size: 16pt;">${getTranslation(i18n, "ESGHighlights.label", language)}</span></strong></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[ ${getTranslation(i18n, "ReportGenImageText.label", language)} ]</strong></span></p>
          </div>`,
        },
      ]
    }
  }

  static getAboutCompanySection(t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "AbouttheCompany.label", language),
      index: 4,
      sortOrder: 5,
      type: ReportSection.ABOUT_COMPANY,
      pages: [
        {
          name: getTranslation(i18n, "OurPurpose.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.ABOUT_COMPANY,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "AbouttheCompany.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
         
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "OurPurpose.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image3.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
      ]
    }
  }

  static getOurESGApproachSection(t, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "OurESGApproach.label", language),
      index: 5,
      sortOrder: 6,
      type: ReportSection.ESG_APPROACH,
      pages: [
        {
          name: getTranslation(i18n, "KeyStakeholderIssues.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.OTHERS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "OurESGApproach.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "KeyStakeholderIssues.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image3.webp" alt=""  width="100%" height="200"></p>
        
          </div>`,
        },
        {
          name: getTranslation(i18n, "ESGGoals.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.ESG_GOALS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "OurESGApproach.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "ESGGoals.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image3.webp" alt=""  width="100%" height="200"></p>
          
          </div>`,
        },
      ]
    }
  }

  static getEnvironmentSection(topics = [], language = 'en', i18n = null) {
    if (topics?.length > 0) {
      const section = {
        name: getTranslation(i18n, "Environment.label", language),
        index: 6,
        sortOrder: 7,
        type: ReportSection.ENVIRONMENT,
        pages: [
          {
            name: getTranslation(i18n, "Overview.label", language),
            index: 0,
            sortOrder: 1,
            type: ReportPageType.ENV_OVERVIEW,
            html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
            <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Environment.label", language)}</strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
            <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
            </div>`,
          }
        ]
      };
      topics?.sort((a, b) => a.sortOrder - b.sortOrder)?.forEach((topic, index) => {
        section.pages.push({
          name: topic.name,
          index: index + 1,
          sortOrder: index + 2,
          type: ReportPageType.ENVIRONMENT,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Environment.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${topic.name}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>
          `,
          json: topic
        },)
      });
      return section;
    }
    return {
      name: getTranslation(i18n, "Environment.label", language),
      index: 6,
      sortOrder: 7,
      type: ReportSection.ENVIRONMENT,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.ENV_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Environment.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Energy.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.ENERGY,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Environment.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Energy.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>
          `,
        },
        {
          name: getTranslation(i18n, "CarbonEmissions.label", language),
          index: 2,
          sortOrder: 3,
          type: ReportPageType.CARBON_EMISSION,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Environment.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "CarbonEmissions.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Waste.label", language),
          index: 3,
          sortOrder: 4,
          type: ReportPageType.WASTE,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Environment.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Waste.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "WaterConsumption.label", language),
          index: 4,
          sortOrder: 5,
          type: ReportPageType.WATER,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Environment.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "WaterConsumption.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>`,
        },
      ]
    }
  }

  static getSocialSection(topics = [], language = 'en', i18n = null) {
    if (topics?.length > 0) {
      const section = {
        name: getTranslation(i18n, "Social.label", language),
        index: 7,
        sortOrder: 8,
        type: ReportSection.SOCIAL,
        pages: [
          {
            name: getTranslation(i18n, "Overview.label", language),
            index: 0,
            sortOrder: 1,
            type: ReportPageType.SOCIAL_OVERVIEW,
            html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
            <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Social.label", language)}</strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
            <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
            </div>`,
          }
        ]
      };
      topics?.sort((a, b) => a.sortOrder - b.sortOrder)?.forEach((topic, index) => {
        section.pages.push({
          name: topic.name,
          index: index + 1,
          sortOrder: index + 2,
          type: ReportPageType.SOCIAL,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Social.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${topic.name}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>
          `,
          json: topic
        },)
      });
      return section;
    }
    return {
      name: getTranslation(i18n, "Social.label", language),
      index: 7,
      sortOrder: 8,
      type: ReportSection.SOCIAL,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.SOCIAL_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Social.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Customers.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.CUSTOMERS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Social.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Customers.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>

          <p style="font-size: 16px;"><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Community.label", language),
          index: 2,
          sortOrder: 3,
          type: ReportPageType.COMMUNITY,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Social.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Community.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Employees.label", language),
          index: 3,
          sortOrder: 4,
          type: ReportPageType.EMPLOYEES,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Social.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Employees.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "Suppliers.label", language),
          index: 4,
          sortOrder: 5,
          type: ReportPageType.SUPPLIERS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Social.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "Suppliers.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_S.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
      ]
    }
  }

  static getGovernanceSection(topics = [], language = 'en', i18n = null) {
    if (topics?.length > 0) {
      const section = {
        name: getTranslation(i18n, "Governance.label", language),
        index: 8,
        sortOrder: 9,
        type: ReportSection.GOVERNANCE,
        pages: [
          {
            name: getTranslation(i18n, "Overview.label", language),
            index: 0,
            sortOrder: 1,
            type: ReportPageType.GOV_OVERVIEW,
            html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
            <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Governance.label", language)}</strong></span></h4>
            <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
            <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_G.webp" alt="" width="100%" height="200"></p>
            </div>`,
          }
        ]
      };
      topics?.sort((a, b) => a.sortOrder - b.sortOrder)?.forEach((topic, index) => {
        section.pages.push({
          name: topic.name,
          index: index + 1,
          sortOrder: index + 2,
          type: ReportPageType.GOVERNANCE,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${topic.name}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image_E.webp" alt=""  width="100%" height="200"></p>
          </div>
          `,
          json: topic
        },)
      });
      return section;
    }
    return {
      name: getTranslation(i18n, "Governance.label", language),
      index: 8,
      sortOrder: 9,
      type: ReportSection.GOVERNANCE,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.GOV_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4><span style="font-size: 18pt; color: rgb(45, 194, 107);"><strong>${getTranslation(i18n, "Governance.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <p style="text-align: center;"><img src="${BASE_URL_DOMAIN}/report/Report_image_G.webp" alt="" width="100%" height="200"></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "ESGGovernance.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.ESG_GOV,
          html: `<div style="font-family: Arial, sans-serif;">
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "ESGGovernance.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "BoardGovernance.label", language),
          index: 2,
          sortOrder: 3,
          type: ReportPageType.BOARD_GOV,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "BoardGovernance.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "HumanRights.label", language),
          index: 3,
          sortOrder: 4,
          type: ReportPageType.HUMAN_RIGHTS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "HumanRights.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "EthicsCompliance.label", language),
          index: 4,
          sortOrder: 5,
          type: ReportPageType.ETHICS_COMP,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "EthicsCompliance.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "TaxPolicy.label", language),
          index: 5,
          sortOrder: 6,
          type: ReportPageType.TAX_POLICY,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "TaxPolicy.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
        {
          name: getTranslation(i18n, "BusinessContinuityDisasterRecovery.label", language),
          index: 6,
          sortOrder: 7,
          type: ReportPageType.BC_DIS_RECOVERY,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "Governance.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <h4 style="text-align: left;"><span style="font-size: 16pt;"><strong>${getTranslation(i18n, "BusinessContinuityDisasterRecovery.label", language)}</strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, "ReportGenImageText.label", language)}]</strong></span></p>
          </div>`,
        },
      ]
    }
  }

  static getESGDisclosureSection(language = 'en', i18n = null) {
    return {
      name: getTranslation(i18n, "ESGContentIndex.label", language),
      index: 9,
      sortOrder: 10,
      type: ReportSection.ESG_INDEX,
      pages: [
        {
          name: getTranslation(i18n, "GRI.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.METRIC_DATA,
          html: ReportingUtils.getMetricDataPageDefaultHTML(),
        },
        {
          name: getTranslation(i18n, "ContentIndex.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.SUST_CONTENT_INDEX,
          html: ReportingUtils.getSustContentIndexPageDefaultHTML(),
        },
      ]
    }
  }

  static getBackCover(logoURL, language = 'en', i18n = null) {
    const imageURL = logoURL ?? `${BASE_URL_DOMAIN}/logo-esg.png`;
    return {
      name: 'Root',
      index: 10,
      sortOrder: 11,
      type: ReportSection.ROOT,
      pages: [
        {
          name: getTranslation(i18n, "BackCover.label", language),
          sortOrder: 0,
          index: 1,
          type: ReportPageType.OTHERS,
          html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
          <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">${getTranslation(i18n, "BackCover.label", language)}</span></strong></span></h4>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReportGenText.label", language)} ]</em></span></p>
          <p><img src="${imageURL}" alt="" width="320" height="50"></p>
          <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, "ReplacePhotoWithOwn.label", language)} ]</em></span></p>
          </div>`,
        },
      ]
    };
  }

  static getCodesByPageType(type) {
    switch (type) {
      case ReportPageType.ENERGY: return [
        "ECD004", "ECD004a", "ECD004b", "ECD004c", "ECD004d",
        "ECD004e", "ECD004f", "ECD004g", "ECD004h", "ECD004i", "ECD004j", "ECD004k", "ECD005", "ECD006"
      ];
      case ReportPageType.CARBON_EMISSION: return [
        "ECD010", "ECD011", "ECD012", "ECD013", "ECD014", "ECD015", "ECD016"
      ];
      case ReportPageType.WATER: return [
        "ECD007", "ECD007a", "ECD007b", "ECD007c", "ECD007d", "ECD007e", "ECD007f",
        "ECD007g", "ECD007h", "ECD007i", "ECD007j", "ECD008", "ECD008a",
        "ECD008b", "ECD008c", "ECD008d", "ECD008e", "ECD008f", "ECD008g", "ECD008h", "ECD008i",
        "ECD008j", "ECD008k", "ECD009", "ECD009a", "ECD009b", "ECD009c",

      ];
      case ReportPageType.WASTE: return [
        "ECD017", "ECD018", "ECD018a", "ECD018b", "ECD018c", "ECD018d", "ECD018e", "ECD018f", "ECD019", "ECD019a",
        "ECD019b", "ECD019c", "ECD019d"

      ];
      case ReportPageType.CUSTOMERS: return [
        "ECD034", "ECD035", "ECD036", "ECD037", "ECD038", "ECD039"

      ];
      case ReportPageType.COMMUNITY: return ["ECD041"];
      case ReportPageType.EMPLOYEES: return [
        "ECD022", "ECD022a", "ECD023", "ECD024", "ECD024a",
        "ECD025", "ECD025a", "ECD025b", "ECD026", "ECD026a", "ECD026b", "ECD026c", "ECD026d", "ECD026e",
        "ECD027", "ECD027a", "ECD028", "ECD029", "ECD029a", "ECD029b", "ECD029c",
        "ECD030", "ECD030a", "ECD030b", "ECD030c", "ECD030d", "ECD030e", "ECD030f", "ECD030g", "ECD030h",
        "ECD030i", "ECD030j", "ECD030k", "ECD030k", "ECD031", "ECD031a", "ECD031b", "ECD031c"
      ];
      case ReportPageType.SUPPLIERS:
        return ["ECD020", "ECD021", "ECD033", "ECD042"];
      case ReportPageType.HUMAN_RIGHTS: return [
        "ECD032"

      ];
      case ReportPageType.ETHICS_COMP: return [
        "ECD043", "ECD044", "ECD045", "ECD046",
      ];
      case ReportPageType.TAX_POLICY:
      default:
        return [];
    }
  }

  static getSubsectionNameForType(type) {
    switch (type) {
      case ReportPageType.ENERGY: return 'energy';
      case ReportPageType.CARBON_EMISSION: return 'emission';
      case ReportPageType.WATER: return 'water';
      case ReportPageType.WASTE: return 'waste';
      case ReportPageType.ENV_OVERVIEW:
      case ReportPageType.SOCIAL_OVERVIEW:
      case ReportPageType.GOV_OVERVIEW:
        return 'overview';
      case ReportPageType.CUSTOMERS: return 'customers';
      case ReportPageType.COMMUNITY: return 'community';
      case ReportPageType.EMPLOYEES: return 'Employees';
      case ReportPageType.SUPPLIERS: return 'suppliers';
      case ReportPageType.ESG_GOV: return 'esg governance';
      case ReportPageType.BOARD_GOV: return 'board governance';
      case ReportPageType.HUMAN_RIGHTS: return 'human rights';
      case ReportPageType.ETHICS_COMP: return 'ethics and compliance';
      case ReportPageType.TAX_POLICY: return 'tax policy';
      case ReportPageType.BC_DIS_RECOVERY:
      default: return '';
    }
  }

  static getQualitativeMetricCodesByPageType(type) {
    switch (type) {
      case ReportPageType.ENERGY: return [
        "ECDQ002", "ECDQ002a", "ECDQ002b", "ECDQ002c",
        "ECDQ003", "ECDQ003a", "ECDQ003b", "ECDQ003c", "ECDQ004", "ECDQ004a", "ECDQ004b", "ECDQ004c", "ECDQ004d",
        "ECDQ005", "ECDQ005a", "ECDQ005b", "ECDQ005c", "ECDQ005d", "ECDQ006", "ECDQ006a", "ECDQ006b", "ECDQ006c",
        "ECDQ302", "ECDQ302a", "ECDQ302b", "ECDQ302c", "ECDQ302d", "ECDQ302e", "ECDQ302f", "ECDQ302g"
      ];
      case ReportPageType.CARBON_EMISSION: return [
        "ECDQ016", "ECDQ016a", "ECDQ016b", "ECDQ016c", "ECDQ016d", "ECDQ016e", "ECDQ017",
        "ECDQ017a", "ECDQ017b", "ECDQ017c", "ECDQ017d", "ECDQ017e", "ECDQ018", "ECDQ018a",
        "ECDQ018b", "ECDQ018c", "ECDQ018d", "ECDQ018e", "ECDQ019", "ECDQ019a", "ECDQ019b",
        "ECDQ019c", "ECDQ019d", "ECDQ020", "ECDQ020a", "ECDQ020b", "ECDQ020c", "ECDQ020d",
        "ECDQ021", "ECDQ021a", "ECDQ021b", "ECDQ021c",
        "ECDQ304", "ECDQ304a", "ECDQ304b", "ECDQ304c", "ECDQ304d", "ECDQ304e", "ECDQ304f", "ECDQ304g"
      ];
      case ReportPageType.WATER: return [
        "ECDQ007", "ECDQ007a", "ECDQ007b", "ECDQ007c", "ECDQ007d", "ECDQ007e", "ECDQ007f",
        "ECDQ008", "ECDQ008a", "ECDQ008b", "ECDQ009", "ECDQ009a", "ECDQ009b", "ECDQ010",
        "ECDQ010a", "ECDQ010b", "ECDQ010c", "ECDQ011", "ECDQ011a", "ECDQ011b",
        "ECDQ303", "ECDQ303a", "ECDQ303b", "ECDQ303c", "ECDQ303d", "ECDQ303e", "ECDQ303f", "ECDQ303g"
      ];
      case ReportPageType.WASTE: return [
        "ECDQ022", "ECDQ022a", "ECDQ022b", "ECDQ023", "ECDQ023a", "ECDQ023b", "ECDQ023c", "ECDQ023d",
        "ECDQ024", "ECDQ024a", "ECDQ024b", "ECDQ025", "ECDQ025a", "ECDQ025b", "ECDQ026", "ECDQ026a", "ECDQ026b",
        "ECDQ305", "ECDQ305a", "ECDQ305b", "ECDQ305c", "ECDQ305d", "ECDQ305e", "ECDQ305f", "ECDQ305g"
      ];
      case ReportPageType.CUSTOMERS: return [
        "ECDQ047", "ECDQ047a", "ECDQ047b",
        "ECDQ048", "ECDQ048a", "ECDQ048b", "ECDQ049", "ECDQ049a", "ECDQ049b", "ECDQ050", "ECDQ050a", "ECDQ050b",
        "ECDQ051", "ECDQ051a", "ECDQ051b",
        "ECDQ314", "ECDQ314a", "ECDQ314b", "ECDQ314c", "ECDQ314d", "ECDQ314e", "ECDQ314f", "ECDQ314g",
        "ECDQ315", "ECDQ315a", "ECDQ315b", "ECDQ315c", "ECDQ315d", "ECDQ315e", "ECDQ315f", "ECDQ315g",
        "ECDQ316", "ECDQ316a", "ECDQ316b", "ECDQ316c", "ECDQ316d", "ECDQ316e", "ECDQ316f", "ECDQ316g"
      ];
      case ReportPageType.COMMUNITY: return [
        "ECDQ044", "ECDQ044a", "ECDQ044b",
        "ECDQ045", "ECDQ045a", "ECDQ045b",
        "ECDQ055", "ECDQ055a", "ECDQ055b", "ECDQ055c", "ECDQ055d",
        "ECDQ322", "ECDQ322a", "ECDQ322b", "ECDQ322c", "ECDQ322d", "ECDQ322e", "ECDQ322f", "ECDQ322g",
        "ECDQ323", "ECDQ323a", "ECDQ323b", "ECDQ323c", "ECDQ323d", "ECDQ323e", "ECDQ323f", "ECDQ323g",
        "ECDQ328", "ECDQ328a", "ECDQ328b", "ECDQ328c", "ECDQ328d", "ECDQ328e", "ECDQ328f", "ECDQ328g"
      ];
      case ReportPageType.EMPLOYEES: return [
        "ECDQ028", "ECDQ028a", "ECDQ028b", "ECDQ028c", "ECDQ029", "ECDQ029a", "ECDQ029b", "ECDQ029c",
        "ECDQ029d", "ECDQ029e", "ECDQ030", "ECDQ030a", "ECDQ030b", "ECDQ031", "ECDQ031a", "ECDQ031b",
        "ECDQ031c", "ECDQ032", "ECDQ032a", "ECDQ032b", "ECDQ033", "ECDQ033a", "ECDQ033b", "ECDQ033c",
        "ECDQ034", "ECDQ034a", "ECDQ034b", "ECDQ034c", "ECDQ035", "ECDQ035a", "ECDQ035b", "ECDQ036",
        "ECDQ036a", "ECDQ036b", "ECDQ036c", "ECDQ036d", "ECDQ036e", "ECDQ037", "ECDQ037a", "ECDQ037b", "ECDQ037c",
        "ECDQ038", "ECDQ038a", "ECDQ038b", "ECDQ038c", "ECDQ038", "ECDQ038a", "ECDQ038b", "ECDQ038c",
        "ECDQ043", "ECDQ043a", "ECDQ043b",
        "ECDQ308", "ECDQ308a", "ECDQ308b", "ECDQ308c", "ECDQ308d", "ECDQ308e", "ECDQ308f", "ECDQ308g",
        "ECDQ309", "ECDQ309a", "ECDQ309b", "ECDQ309c", "ECDQ309d", "ECDQ309e", "ECDQ309f", "ECDQ309g",
        "ECDQ310", "ECDQ310a", "ECDQ310b", "ECDQ310c", "ECDQ310d", "ECDQ310e", "ECDQ310f", "ECDQ310g",
        "ECDQ311", "ECDQ311a", "ECDQ311b", "ECDQ311c", "ECDQ311d", "ECDQ311e", "ECDQ311f", "ECDQ311g",
        "ECDQ321", "ECDQ321a", "ECDQ321b", "ECDQ321c", "ECDQ321d", "ECDQ321e", "ECDQ321f", "ECDQ321g"
      ];
      case ReportPageType.SUPPLIERS: return [
        "ECDQ307", "ECDQ307a", "ECDQ307b", "ECDQ307c", "ECDQ307d", "ECDQ307e", "ECDQ307f", "ECDQ307g",
        "ECDQ313", "ECDQ313a", "ECDQ313b", "ECDQ313c", "ECDQ313d", "ECDQ313e", "ECDQ313f", "ECDQ313g",
        "ECDQ329", "ECDQ329a", "ECDQ329b", "ECDQ329c", "ECDQ329d", "ECDQ329e", "ECDQ329f", "ECDQ329g"
      ];
      case ReportPageType.HUMAN_RIGHTS: return [
        "ECDQ039", "ECDQ039a", "ECDQ039b",
        "ECDQ027", "ECDQ027a", "ECDQ027b", "ECDQ027c",
        "ECDQ040", "ECDQ040a", "ECDQ040b", "ECDQ040c",
        "ECDQ041", "ECDQ041a", "ECDQ041b",
        "ECDQ042", "ECDQ042a", "ECDQ042b", "ECDQ042c",
        "ECDQ312", "ECDQ312a", "ECDQ312b", "ECDQ312c", "ECDQ312d", "ECDQ312e", "ECDQ312f", "ECDQ312g",
        "ECDQ317", "ECDQ317a", "ECDQ317b", "ECDQ317c", "ECDQ317d", "ECDQ317e", "ECDQ317f", "ECDQ317g",
        "ECDQ318", "ECDQ318a", "ECDQ318b", "ECDQ318c", "ECDQ318d", "ECDQ318e", "ECDQ318f", "ECDQ318g",
        "ECDQ319", "ECDQ319a", "ECDQ319b", "ECDQ319c", "ECDQ319d", "ECDQ319e", "ECDQ319f", "ECDQ319g",
        "ECDQ320", "ECDQ320a", "ECDQ320b", "ECDQ320c", "ECDQ320d", "ECDQ320e", "ECDQ320f", "ECDQ320g"
      ];
      case ReportPageType.ETHICS_COMP: return [
        "ECDQ058", "ECDQ058a", "ECDQ058b", "ECDQ059", "ECDQ059a", "ECDQ059b",
        "ECDQ060", "ECDQ060a", "ECDQ060b",
        "ECDQ326", "ECDQ326a", "ECDQ326b", "ECDQ326c", "ECDQ326d", "ECDQ326e", "ECDQ326f", "ECDQ326g",
        "ECDQ327", "ECDQ327a", "ECDQ327b", "ECDQ327c", "ECDQ327d", "ECDQ327e", "ECDQ327f", "ECDQ327g"
      ];
      case ReportPageType.TAX_POLICY: return [
        "ECDQ061", "ECDQ061a", "ECDQ061b", "ECDQ062", "ECDQ062a", "ECDQ062b", "ECDQ062c", "ECDQ062d",
        "ECDQ063", "ECDQ063a", "ECDQ063b", "ECDQ064", "ECDQ064a", "ECDQ064b", "ECDQ064c", "ECDQ064d",
        "ECDQ331", "ECDQ331a", "ECDQ331b", "ECDQ331c", "ECDQ331d", "ECDQ331e", "ECDQ331f", "ECDQ331g"
      ];
      default:
        return null;
    }
  }

  static getPageTypeFromQualitativeMetricCode(code) {
    const energyCodes = [
      "ECDQ002", "ECDQ002a", "ECDQ002b", "ECDQ002c",
      "ECDQ003", "ECDQ003a", "ECDQ003b", "ECDQ003c", "ECDQ004", "ECDQ004a", "ECDQ004b", "ECDQ004c", "ECDQ004d",
      "ECDQ005", "ECDQ005a", "ECDQ005b", "ECDQ005c", "ECDQ005d", "ECDQ006", "ECDQ006a", "ECDQ006b", "ECDQ006c"
    ];
    const emissionCodes = [
      "ECDQ016", "ECDQ016a", "ECDQ016b", "ECDQ016c", "ECDQ016d", "ECDQ016e", "ECDQ017",
      "ECDQ017a", "ECDQ017b", "ECDQ017c", "ECDQ017d", "ECDQ017e", "ECDQ018", "ECDQ018a",
      "ECDQ018b", "ECDQ018c", "ECDQ018d", "ECDQ018e", "ECDQ019", "ECDQ019a", "ECDQ019b",
      "ECDQ019c", "ECDQ019d", "ECDQ020", "ECDQ020a", "ECDQ020b", "ECDQ020c", "ECDQ020d",
      "ECDQ021", "ECDQ021a", "ECDQ021b", "ECDQ021c"
    ];
    const waterCodes = [
      "ECDQ007", "ECDQ007a", "ECDQ007b", "ECDQ007c", "ECDQ007d", "ECDQ007e", "ECDQ007f",
      "ECDQ008", "ECDQ008a", "ECDQ008b", "ECDQ009", "ECDQ009a", "ECDQ009b", "ECDQ010",
      "ECDQ010a", "ECDQ010b", "ECDQ010c", "ECDQ011", "ECDQ011a", "ECDQ011b"
    ];
    const wasteCodes = [
      "ECDQ022", "ECDQ022a", "ECDQ022b", "ECDQ023", "ECDQ023a", "ECDQ023b", "ECDQ023c", "ECDQ023d",
      "ECDQ024", "ECDQ024a", "ECDQ024b", "ECDQ025", "ECDQ025a", "ECDQ025b", "ECDQ026", "ECDQ026a", "ECDQ026b"
    ];
    const customerCodes = [
      "ECDQ047", "ECDQ047a", "ECDQ047b",
      "ECDQ048", "ECDQ048a", "ECDQ048b", "ECDQ049", "ECDQ049a", "ECDQ049b", "ECDQ050", "ECDQ050a", "ECDQ050b",
      "ECDQ051", "ECDQ051a", "ECDQ051b"
    ];
    const communityCodes = [
      "ECDQ044", "ECDQ044a", "ECDQ044b",
      "ECDQ045", "ECDQ045a", "ECDQ045b",
      "ECDQ055", "ECDQ055a", "ECDQ055b", "ECDQ055c", "ECDQ055d"
    ];
    const employeeCodes = [
      "ECDQ028", "ECDQ028a", "ECDQ028b", "ECDQ028c", "ECDQ029", "ECDQ029a", "ECDQ029b", "ECDQ029c",
      "ECDQ029d", "ECDQ029e", "ECDQ030", "ECDQ030a", "ECDQ030b", "ECDQ031", "ECDQ031a", "ECDQ031b",
      "ECDQ031c", "ECDQ032", "ECDQ032a", "ECDQ032b", "ECDQ033", "ECDQ033a", "ECDQ033b", "ECDQ033c",
      "ECDQ034", "ECDQ034a", "ECDQ034b", "ECDQ034c", "ECDQ035", "ECDQ035a", "ECDQ035b", "ECDQ036",
      "ECDQ036a", "ECDQ036b", "ECDQ036c", "ECDQ036d", "ECDQ036e", "ECDQ037", "ECDQ037a", "ECDQ037b", "ECDQ037c",
      "ECDQ038", "ECDQ038a", "ECDQ038b", "ECDQ038c", "ECDQ038", "ECDQ038a", "ECDQ038b", "ECDQ038c",
      "ECDQ043", "ECDQ043a", "ECDQ043b"
    ];
    const supplierCodes = ["ECD020", "ECD021"];
    const hrCodes = [
      "ECDQ039", "ECDQ039a", "ECDQ039b",
      "ECDQ027", "ECDQ027a", "ECDQ027b", "ECDQ027c",
      "ECDQ040", "ECDQ040a", "ECDQ040b", "ECDQ040c",
      "ECDQ041", "ECDQ041a", "ECDQ041b",
      "ECDQ042", "ECDQ042a", "ECDQ042b", "ECDQ042c"
    ];
    const ethicsCodes = [
      "ECDQ058", "ECDQ058a", "ECDQ058b", "ECDQ059", "ECDQ059a", "ECDQ059b",
      "ECDQ060", "ECDQ060a", "ECDQ060b"
    ];
    const taxPolicyCodes = [
      "ECDQ061", "ECDQ061a", "ECDQ061b", "ECDQ062", "ECDQ062a", "ECDQ062b", "ECDQ062c", "ECDQ062d",
      "ECDQ063", "ECDQ063a", "ECDQ063b", "ECDQ064", "ECDQ064a", "ECDQ064b", "ECDQ064c", "ECDQ064d"
    ];
    if (energyCodes.indexOf(code) >= 0) {
      // return ReportPageType.ENERGY;
      return "Energy";
    } else if (emissionCodes.indexOf(code) >= 0) {
      // return ReportPageType.CARBON_EMISSION;
      return "Carbon Emission";
    } else if (waterCodes.indexOf(code) >= 0) {
      // return ReportPageType.WATER;
      return "Water";
    } else if (wasteCodes.indexOf(code) >= 0) {
      // return ReportPageType.WASTE;
      return "Waste";
    } else if (customerCodes.indexOf(code) >= 0) {
      // return ReportPageType.CUSTOMERS;
      return "Customers";
    } else if (communityCodes.indexOf(code) >= 0) {
      // return ReportPageType.COMMUNITY;
      return "Community";
    } else if (employeeCodes.indexOf(code) >= 0) {
      // return ReportPageType.EMPLOYEES;
      return "Employees";
    } else if (supplierCodes.indexOf(code) >= 0) {
      // return ReportPageType.SUPPLIERS;
      return "Suppliers";
    } else if (hrCodes.indexOf(code) >= 0) {
      // return ReportPageType.HUMAN_RIGHTS;
      return "Human Rights";
    } else if (ethicsCodes.indexOf(code) >= 0) {
      // return ReportPageType.ETHICS_COMP;
      return "Ethics & Compliance";
    } else if (taxPolicyCodes.indexOf(code) >= 0) {
      // return ReportPageType.TAX_POLICY;
      return "Tax Policy";
    } else {
      return null;
    }
  }

  static getGDQuestionCodesByPageType(type) {
    switch (type) {
      case ReportPageType.ESG_GOV: return [
        "ECDG022", "ECDG023", "ECDG024", "ECDG025", "ECDG026", "ECDG027"
      ];
      case ReportPageType.BOARD_GOV: return [
        "ECDG009", "ECDG010", "ECDG011", "ECDG012", "ECDG013", "ECDG014", "ECDG017", "ECDG018"
      ];
      default:
        return [];
    }
  }

  static getOverviewPageKeysForSection(type) {
    switch (type) {
      case ReportSection.ENVIRONMENT:
        return ["ENERGY", "EMISSION", "WASTE", "WATER"];
      case ReportSection.SOCIAL:
        return ["CUSTOMERS", "COMMUNITY", "EMPLOYEES", "SUPPLIERS"];
      case ReportSection.GOVERNANCE:
        return ["BOARD_GOVERNANCE", "ESG_GOVERNANCE", "HUMAN_RIGHTS", "ETHICS_AND_COMPLIANCE", "TAX_POLICY"];
      default: return [];
    }
  }

  static getMSGFromCSOPage() {
    return {
      name: 'Message from the Chief Sustainability Officer',
      index: 1,
      sortOrder: 2,
      type: ReportPageType.MSG_FROM_CSO,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Leadership messages</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <h4 style="text-align: left;"><span style="font-size: 16pt; color: rgb(0, 0, 0);"><strong>Message from the Chief Sustainability Officer</strong></span></h4>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Introduction:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Begin with a brief greeting to stakeholders and investors, expressing appreciation for their support and interest in sustainability efforts.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Role Statement:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Outline the role of the CSO in {Company_name}, emphasizing their commitment to driving ESG initiatives and ensuring company-wide adherence to sustainability principles. </span></p>
      <p><span style="color: rgb(126, 140, 141);">ESG Integration:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Describe how ESG factors are integrated into the business activities of {Company_name}. Highlight the company's dedication to maintaining a balance between operational efficiency and sustainable practices.</span></p>
      <p><span style="color: rgb(126, 140, 141);">ESG Achievements Overview:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Detail the outstanding ESG achievements the company has realised in the past fiscal year. This could include key milestones, innovative projects, or significant improvements in environmental, social, and governance metrics. Use data-backed evidence to reinforce achievements. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Impact Analysis:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Describe the impact these achievements have had on the company and its stakeholders. Discuss how these accomplishments have improved the company's reputation, attracted more investors, increased customer satisfaction, or enhanced employee engagement. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Challenges and Learning:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Acknowledge the challenges faced during the implementation of ESG initiatives and elaborate on how they have been addressed. Highlight the lessons learned and how they will inform future strategies.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Future Outlook:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's future goals and strategies in relation to ESG. Provide a roadmap of planned initiatives, demonstrating the company's ongoing commitment to sustainability.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Gratitude and Conclusion:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclude the message with an expression of gratitude towards employees, stakeholders, and partners for their contribution to the company's ESG achievements.</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><img src="${BASE_URL_DOMAIN}/report/customer.png" alt="" width="320" height="240"></p>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace a photo with your own ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);">Signature:</span></p>
      <p><span style="color: rgb(126, 140, 141);">End the message with the CSO's signature, reinforcing the personal commitment and accountability in leading the company's sustainability agenda.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Ensure that the tone of the message is engaging, transparent, and forward-looking, fostering confidence in the company's ESG strategies.</span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getSuppliersPage() {
    return {
      name: 'Suppliers',
      index: 4,
      sortOrder: 5,
      type: ReportPageType.SUPPLIERS,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Suppliers</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Introduction:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Briefly outline the company's commitment to implementing sustainable practices across the supply chain. Explain the importance of this commitment to both the company and its stakeholders. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Policies and Guidelines:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Provide an overview of the company's supply chain policies and guidelines. Highlight the key principles and objectives that guide these policies, such as ethical sourcing, human rights considerations, environmental impact reduction, etc.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Embedding Sustainability in Business Operations:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Describe how the company integrates sustainability into its business operations, particularly in areas like risk assessment and procurement. Provide specific examples to demonstrate this, such as adopting green procurement practices, integrating ESG risks into the company's overall risk assessment framework, etc. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Supplier Assessment:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Explain the procedures and criteria used for assessing suppliers' environmental and social performances. Elaborate on how the company ensures suppliers' compliance with its sustainability standards.</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Actions Based on Assessment Results:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the actions that the company takes based on the results of its supplier assessments. This may include actions such as providing feedback and guidance to suppliers, terminating contracts with non-compliant suppliers, etc.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Case Study or Anecdote:</span></p>
      <p><span style="color: rgb(126, 140, 141);">If possible, include a case study or anecdote to illustrate how the company has successfully implemented its supply chain sustainability practices. This will help demonstrate the effectiveness of the company's efforts in a concrete and relatable way.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Spend on Local Suppliers:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Provide data on the company's spending on local suppliers. Discuss the company's rationale for supporting local suppliers and the benefits this brings to the local economy and community. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Future Plans:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's future plans for further improving its supply chain sustainability. This could include things like setting new sustainability targets for suppliers, investing in technologies to improve supply chain transparency, etc.</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
      <p><span style="color: rgb(126, 140, 141);">Infographics and Visual Aids:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Utilize graphs, charts, and infographics to visually represent data and trends. This makes the report more engaging and easier to understand.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclusion:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclude the section by reiterating the importance of supply chain sustainability to the company and its stakeholders. Encourage stakeholders to provide feedback and get involved in the company's sustainability efforts. </span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getHumanRightsPage() {
    return {
      name: 'Human Rights',
      index: 3,
      sortOrder: 4,
      type: ReportPageType.HUMAN_RIGHTS,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Human Rights</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Introduction:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Begin the section with a brief overview of the company's commitment to human rights. This could include the policies adopted and the company's dedication to ensuring a safe, fair, and inclusive work environment. Highlight the company's proactive approach to mitigate any risks that could lead to human rights violations in the operations and supply chain. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Child Labour and Young Workers:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Give details about the company's policy on child labour and protecting young workers. Mention specific measures taken to prevent child labour and the safeguards in place to protect young employees from hazardous work. Include facts, figures, or case studies showcasing the effectiveness of these measures.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Forced or Compulsory Labour:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company&rsquo;s stance on forced or compulsory labour, including the steps taken to ensure such practices are not part of the operations and supply chains. Provide insights into the company's strategy for identifying and mitigating this risk.</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Security Personnel Training:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Provide information on training programs for security personnel in the context of the company's human rights policies. Highlight the frequency, content, and impact of these training programs. It would be beneficial to provide statistics on the number of personnel trained, and the improvements observed in their conduct.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Supplier's Assessment:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's approach to supplier assessment. Showcase the measures in place to evaluate and monitor suppliers for potential risk of human rights violations. Provide details on the frequency and results of these assessments.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Future Initiatives:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Towards the end, outline future initiatives, plans, and goals to strengthen the company's commitment to human rights. This may include new policies, partnerships, or training programs. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
      <p><span style="color: rgb(126, 140, 141);">Conclusion:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclude by reaffirming the company's commitment to human rights. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Visual Aids:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Use infographics, charts, and diagrams to illustrate points and maintain reader engagement. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Remember to maintain a positive yet realistic tone throughout the section. Use clear, concise language, and avoid jargon to ensure the report is accessible to all stakeholders. </span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getEthicsPage() {
    return {
      name: 'Ethics & Compliance',
      index: 4,
      sortOrder: 5,
      type: ReportPageType.ETHICS_COMP,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Ethics &amp; Compliance</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Start the section by providing an overview of the company's commitment to ethical business practices, highlighting how the company's operations align with the industry norms and regional regulations. This should provide a broad understanding of the company's ethical stance and commitment to compliance.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's anti-corruption efforts. Explain the measures taken to prevent corruption within the company, such as training programs for employees, regular audits, and zero-tolerance policies. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss any legal actions that the company may have faced related to anti-competitive behavior, anti-trust, and monopoly practices. Be transparent about the circumstances, outcomes, and steps taken to prevent such issues in the future. If the company has faced no such actions, state this clearly to underscore the company's ethical standing. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">If applicable, disclose details about political contributions made by the company. Be sure to emphasize that these contributions are in line with the company's values and are made transparently and ethically. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's commitment to safeguard customer data. Explain the measures taken to prevent data theft and leakage, such as investing in advanced cybersecurity infrastructure, regular system updates, and employee training on data handling. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Highlight any certifications or recognitions the company has received that validate its commitment to ethics and compliance. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
      <p><span style="color: rgb(126, 140, 141);">Data Visualization:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Consider including charts, graphs, and tables to visualize the data and make it easier for stakeholders to understand. </span></p>
      <p><span style="color: rgb(126, 140, 141);">End the section on a forward-looking note, detailing any planned improvements or initiatives aimed at further strengthening the company's ethical and compliant standing. This will reassure stakeholders of the company's continuous commitment to ethics and compliance. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Remember to maintain a professional tone throughout, and ensure all claims are supported with evidence or specific examples. This will enhance the credibility of the report.</span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getTaxPolicyPage() {
    return {
      name: 'Tax Policy',
      index: 5,
      sortOrder: 6,
      type: ReportPageType.TAX_POLICY,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Tax Policy</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Introduction:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Begin with a brief and engaging introduction to the company's tax policies, emphasizing the importance of tax strategy in the company's overall financial health and sustainability practices. Connect this to the company's core values, such as transparency, responsibility, and integrity, to underscore the company's commitment to ethical tax management. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Tax Policy Overview:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Outline the fundamental principles, standards, and codes of conduct that guide the company's tax strategy. Ensure to highlight the company's commitment to comply with all relevant tax laws and regulations in the jurisdictions where it operates.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the company's attitude towards tax planning, making it clear that it aligns with business operations and commercial activity. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Governance and Control:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Detail the company's tax governance structure, including roles and responsibilities of key personnel and bodies involved in tax decisions. Discuss the control framework in place to ensure adherence to tax laws and principles, including the frequency and nature of internal and external audits. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Stakeholder Engagement:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Describe the company's approach to engaging with stakeholders on tax matters. This could include regular tax-related updates to shareholders, engagement with tax authorities, or consultation with external advisors. Also, address the steps taken by the company in response to stakeholder concerns related to tax, highlighting the company's commitment to transparency and open communication. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Tax Jurisdictions:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Provide a list of all tax jurisdictions where the entities included in the company's audited consolidated financial statements or in the financial information filed on public record are resident for tax purposes. Where possible, provide a brief explanation of the company's operations in each jurisdiction to offer context. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
      <p><span style="color: rgb(126, 140, 141);">Future Outlook:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclude with an optimistic, forward-looking statement, signaling the company's commitment to maintaining its high standards of tax governance, and its willingness to adapt its tax strategy as necessary in response to changes in the business environment or tax legislation. This will demonstrate the company's proactive approach to tax management and its dedication to sustainable practices. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Data Visualization:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Consider including charts, graphs, and tables to visualize the data and make it easier for stakeholders to understand.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Throughout the section, maintain a tone that is professional, informative, and engaging. Use clear, jargon-free language to ensure the section is accessible to a broad range of stakeholders, and include relevant data where possible to support claims and demonstrate the company's commitment to transparency. </span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getBCDiscoveryPage() {
    return {
      name: 'Business Continuity/Disaster Recovery',
      index: 6,
      sortOrder: 7,
      type: ReportPageType.BC_DIS_RECOVERY,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Business Continuity/Disaster Recovery</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Overview:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Begin by providing a brief overview of the Business Continuity/Disaster Recovery (BC/DR) section, highlighting its importance in the ESG report.</span></p>
      <p><span style="color: rgb(126, 140, 141);">BC/DR Importance:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Detail why BC/DR is essential in the {industry} sector and for {company_name} in particular. Share an overview of the potential impacts of not having comprehensive BC/DR measures in place. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Management Systems and Procedures:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Describe in detail the management systems and procedures implemented by {company_name} to handle BC/DR. Include information on the individuals or teams in charge of these procedures, and their roles and responsibilities.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Testing Regime:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Outline the testing regimes established by {company_name} to ensure the effectiveness of these BC/DR plans. Describe the frequency of these tests, the metrics used to measure success, and any improvements made based on test results. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Third-Party Audits:</span></p>
      <p><span style="color: rgb(126, 140, 141);">If applicable, mention any third-party audits or testing arrangements. Highlight how these third parties help validate the effectiveness of the BC/DR plans and bring an additional layer of credibility. </span></p>
      <p><span style="color: rgb(126, 140, 141);">BS25999 Certification:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Discuss the significance of the BS25999 certification in the BC/DR landscape and its importance for {company_name}. If applicable, mention that {company_name} has received this certification and explain what this means in terms of the company's commitment to best-practice BC/DR procedures. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Case Studies:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Include any specific examples or case studies of when the company's BC/DR plans were put into action, the results, and any subsequent improvements that were made. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Future Plans:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Provide a forward-looking statement about the company's future plans regarding BC/DR, underlining the ongoing commitment to improving these systems and procedures. </span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
      <p><span style="color: rgb(126, 140, 141);">Data Visualization:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Consider including charts, graphs, and tables to visualize the data and make it easier for stakeholders to understand.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Stakeholder Engagement:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Detail how {company_name} engages with stakeholders in relation to BC/DR, from informing them about the plan to involving them in its testing and improvement. </span></p>
      <p><span style="color: rgb(126, 140, 141);">Conclusion:</span></p>
      <p><span style="color: rgb(126, 140, 141);">Summarize the key points made in this section, reinforcing {company_name}'s commitment to robust BC/DR measures and its dedication to continuous improvement in this area.</span></p>
      <p><span style="color: rgb(126, 140, 141);">Remember to use an engaging, optimistic, and informative tone throughout the section, emphasizing the company's dedication and commitment to sustainability and corporate responsibility. </span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    };
  }

  static getContribToUNSDGsPage() {
    return {
      name: 'Contribution to UN SDGs',
      index: 1,
      sortOrder: 2,
      type: ReportPageType.CONTRIB_TO_UN_SDGS,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Our ESG Approach</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <h4 style="text-align: left;"><span style="font-size: 16pt; color: rgb(0, 0, 0);"><strong>Contribution to UN SDGs</strong></span></h4>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><img src="${BASE_URL_DOMAIN}/report/customer.png" alt="" width="320" height="240"></p>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace a photo with your own ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      </td>
      </tr>
      </tbody>
      </table>
      </div>`,
    }
  }

  static getBiodiversityPage() {
    return {
      name: 'Biodiversity',
      index: 1,
      sortOrder: 2,
      type: ReportPageType.BIODIVERSITY,
      html: `<div style="font-family: Arial, sans-serif;" className='RS_Drak_WhiteColor'>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1"><colgroup><col style="width: 33%;"><col style="width: 33%;"><col style="width: 33%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <h4 style="text-align: left;"><span style="font-size: 18pt;"><strong><span style="color: rgb(45, 194, 107);">Biodiversity</span></strong></span></h4>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace with text of your own, just delete it and start typing ]</em></span></p>
      <p><span style="color: rgb(126, 140, 141);"><strong>[Type here an introduction statement about the purpose of this page. ]</strong></span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      <p><span style="color: rgb(126, 140, 141);">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</span></p>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <p><img src="${BASE_URL_DOMAIN}/report/energy.png" alt="" width="320" height="240"></p>
      <p><span style="color: rgb(230, 126, 35);"><em>[ To replace a photo with your own ]</em></span></p>
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; border-spacing: 0px; margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
      <tbody>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      <tr>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><span style="color: rgb(45, 194, 107);"><strong>40%</strong></span><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      <td style="border-width: 0px; vertical-align: top; padding: 10px;">
      <div style="border: 1px solid lightgray; padding: 10px;">
      <p style="text-align: center;">&nbsp;</p>
      <p style="text-align: center;"><strong><span style="color: rgb(45, 194, 107);">1000KW</span></strong><br>Lorem ipsum dolor sit amet.</p>
      </div>
      </td>
      </tr>
      </tbody>
      </table>      
      </td>
      </tr>
      </tbody>
      </table>
      </div>
      `,
    };
  }

  static getHTMLWithFormattedTextOLD({ text, sectionName, pageName }) {
    /**
     * Generally an A4 page is of 8.3 X 11.7 (width X height) inches.
     * We are displaying it in landscape mode. So, the dimension changes to
     * 11.7 X 8.3 (width X height) inches.
     * Each column will have a width of 3.9 inches.
     * 
     * Characters per line = Page Width (in pixels) / Character Width (in pixels)
     * Lines per Page = Page Height (in pixels) / Line Height (in pixels)
     * Total Characters= Characters per Line X Lines per Page
     */
    const charPerLineInCol = 374.4 / 12;
    const linesPerCol = 796.8 / 1.5;
    const totalCharPerCol = charPerLineInCol * linesPerCol;

    let htmlString = `
      <div style="font-family: Arial, sans-serif; font-weight: 400;font-size:12px;">
      <table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1">
      <colgroup><col style="width: 3.9in;"><col style="width: 3.9in;"><col style="width: 3.9in;"></colgroup>
      <tbody>
    `;

    // Parse text into sentences
    const sentences = text.split(/[.!?]/).filter(Boolean);
    // Calculate the number of sentences per column
    const sentencesPerColumn = Math.ceil(sentences.length / 3);
    htmlString += '<tr>';
    // Iterate through each column
    for (let i = 0; i < 3; i++) {
      // Calculate the start and end index for sentences in this column
      const startIndex = i * sentencesPerColumn;
      const endIndex = Math.min((i + 1) * sentencesPerColumn, sentences.length);
      // Extract sentences for this column
      const columnSentences = sentences.slice(startIndex, endIndex);
      // Create a cell in the table for this column
      htmlString += `<td style="border-width: 0px; vertical-align: top; padding: 5px;">`;
      if (i === 0) {
        htmlString += `
          <h4 style="text-align: left;">
            <span style="font-family: Arial, sans-serif;font-size: 18pt;">
              <strong><span style="color: rgb(45, 194, 107);">${sectionName}</span></strong>
            </span>
          </h4>
          <h4 style="text-align: left;">
            <span style="font-family: Arial, sans-serif;font-size: 16pt; color: rgb(0, 0, 0);"><strong>${pageName}</strong></span>
          </h4>
        `;
      }
      let charCount = 0;
      for (let j = 0; j < text.length; j++) {
        const character = text[j];
      }
      // Add each sentence to the cell
      columnSentences.forEach(sentence => {
        htmlString += `<p>${sentence.trim()}</p>`;
      });
      htmlString += `</td>`;
    }
    htmlString += '</tr>';
    htmlString += '</tbody></table></div>';
    return htmlString;
  }


  static getHTMLWithFormattedText({ text, sectionName, pageName }) {
    /**
     * Generally an A4 page is of 8.3 X 11.7 (width X height) inches.
     * We are displaying it in landscape mode. So, the dimension changes to
     * 11.7 X 8.3 (width X height) inches.
     * Each column will have a width of 3.9 inches.
     * 
     * Characters per line = Page Width (in pixels) / Character Width (in pixels)
     * Lines per Page = Page Height (in pixels) / Line Height (in pixels)
     * Total Characters= Characters per Line X Lines per Page
     */
    // Note thate 3.5 is used instead of 3.9 as an offset of 0.2 is applied for padding - 0.2 + 0.2 = 0.4
    const charPerLineInCol = Math.round((3.5 * 96) / 12);
    const linesPerCol = Math.round((8 * 6) / 1.5);// Here also not 8.3, offset 0.3 reduced
    let totalCharPerCol = Math.round(charPerLineInCol * linesPerCol);
    let tableCount = 1;
    if ((text.length / totalCharPerCol) > 3) {
      const val = text.length / totalCharPerCol;
      tableCount = Math.ceil(val / 3);
    } else {
      totalCharPerCol = Math.round(text.length / 3);
    }
    let htmlString = `<div style="font-family: Arial, sans-serif;  font-weight: 400; font-size:12px;">`;
    let charIndex = 0;
    for (let tableCounter = 0; tableCounter < tableCount; tableCounter++) {
      htmlString += `<table style="border-collapse: collapse; width: 100%; border-width: 0px; margin-left: 0px; margin-right: auto; text-align: justify;" border="1">
        <colgroup><col style="width: 3.9in;"><col style="width: 3.9in;"><col style="width: 3.9in;"></colgroup>
        <tbody>`;
      htmlString += '<tr>';
      // Iterate through each column
      for (let i = 0; i < 3; i++) {
        // Create a cell in the table for this column
        htmlString += `<td style="border-width: 0px; vertical-align: top; padding: 0.2in;">`;
        if (i === 0 && tableCounter === 0) {
          htmlString += `
                    <h4 style="text-align: left;">
                        <span style="font-family: Arial, sans-serif;font-size: 18pt;">
                            <strong><span style="color: rgb(45, 194, 107);">${sectionName}</span></strong>
                        </span>
                    </h4>
                    <h4 style="text-align: left;">
                        <span style="font-family: Arial, sans-serif;font-size: 16pt; color: rgb(0, 0, 0);"><strong>${pageName}</strong></span>
                    </h4>
                `;
        }
        let charCount = 0;
        htmlString += `<p>`;
        for (; charIndex < text.length; charIndex++) {
          const character = text[charIndex];
          if (character === '\n') {
            htmlString += `<br>`;
            charCount += Math.round(charPerLineInCol);
          }
          htmlString += character;
          charCount++;
          if (charCount >= totalCharPerCol) {
            const nextCharacter = text[charIndex + 1];
            // If the next character is empty or new line then break the column.
            if (character === '\n' || character === '' || nextCharacter === '\n' || nextCharacter === '') {
              charIndex++;
              break;
            } else {
              // Otherwise complete the word and then break the column.
              htmlString += nextCharacter;
              charIndex++;
            }
          }
        }
        htmlString += `</p></td>`;
      }
      htmlString += '</tr>';
      htmlString += '</tbody></table>';
    }
    htmlString += `</div>`;
    return htmlString;
  }

  static getHTMLWithTextFormattedAsParagraphs({ text }) {
    const lines = text.split('\n');
    let paras = ``;
    lines.forEach((line) => {
      if (Utils.isEmpty(line)) return;

      let lineText = line.replace(
        '<R>',
        '<span style="background-color: bisque;"><span style="color: red;"><strong><<</strong></span>'
      );
      lineText = lineText.replace(
        '</R>',
        '<span style="color: red;"><strong>>></strong></span></span>'
      );
      lineText = lineText.replace('[[', '<span style="color: rgb(230, 126, 35);">[[');
      lineText = lineText.replace(']]', ']]</span>');
      paras += `<p style="font-family: Arial, sans-serif;font-size: 14px;">${lineText}</p>`;
    });
    return `<div>${paras}</div>`;
  }

  static getHTMLWithContentBlock({ name, description }) {
    return `<div>
      <h4 style="text-align: left;"><span style="font-family: Arial, sans-serif;font-size: 16pt;"><strong>${name}</strong></span></h4>
      ${description}
    </div>`;
  }

  static downloadBRSRReportPDF = (inputElement, fileName) => {
    const style = document.createElement("style");
    style.textContent = `			
			.front-page {
				margin-top: 40px !imporant;
				margin-bottom: 40px !imporant;
			}
			.none {  list-style: none; } 
			.fw-bold { font-weight: bold; }
			.ETA_card_Section_head h5 {
					font-size: 16px;
					font-weight: 700;
					color: #0a58ca;
			}
			.FY_class {
				background-color: #0d6efd38;
			}
			.FY_class_sub {
				background-color: #0d6efd1f;
			}
			.FY_class_content {
					background-color: #0d6efd14;
			}
      .GHG_CardMain > .ETA_card_center > .Main_Section > .ETA_card_Section table > tbody > tr {
        border-top: none !important;
      }
		`;
    inputElement.appendChild(style);
    html2pdf()
      .set({
        margin: 10,
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(inputElement)
      .toContainer()
      .save();
  }
  
  static downloadBRSRReportWord = (htmlContent, fileName, options = {}) => {
    const isGhgExport = (fileName || "").startsWith("GHG_");
    const ghgDocxTableP = isGhgExport
      ? `${WORD_DOCX_GHG_TABLE_P_FULL_WIDTH_CSS}${WORD_DOCX_GHG_INNER_IMAGE_CSS}${WORD_DOCX_GHG_COVER_NO_BORDER_CSS}${WORD_DOCX_GHG_COVER_BRSR_TABLE_BORDER_RESET_CSS}`
      : "";
    const bodyHtml = isGhgExport
      ? prepareGhgHtmlForWordExport(htmlContent)
      : htmlContent;

    const buildAndDownload = (embeddedBody) => {
    const htmlString = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <style type="text/css">
          body {
            padding: 0px;
            margin: 0px;
            font-size: 13px;
            color: #424242;
            font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
          }
          .none {  list-style-type: auto; } 
          b, strong {
            font-weight: bolder;
          }
          ${BRSR_EXPORT_TABLE_AND_SPACING_CSS}
          .fw-bold { font-weight: bold; }
          .ETA_card_Section_head h5 {
            font-size: 20px;
            font-weight: 700;
            color: #0a58ca;
          }
          .ETA_card_Section_sub_head {
            margin-bottom: 5px;
            padding-left: 27px;
            position: relative;
            font-size: 15px;
            color: #0a58ca;
          }
          .ETA_card_Section_sub_head .count {
            position: absolute;
            top: 0px;
            left: 0px;
            width: 24px;
            display: block;
            text-align: right;
            margin-right: 5px;
            font-weight: bolder;
            color: #0a58ca;
          }
          th.FY_class {
            background-color: #0d6efd38 !important;
          }
          th.FY_class_sub {
            background-color: #0d6efd1f !important;
          }
          td.FY_class_content {
              background-color: #0d6efd14 !important;
          } 
          .count {
            font-weight: bold;
          }
          .brsr-list-item {
            margin: 2px 2px 2px 15px;
            margin: 0px;
            margin-bottom: 10px;
          }          
          .brsr-sublist {
            margin-left: 0px;
            margin-left: 15px;
          }
          .mt-2 {
            margin-top: 7.5px;
          }
          .mb-4 {
            margin-bottom: 7.5px;
          }
          .ghg-dark-highlight {
            background-color: #00b05f;
            color: white;
          }
          .ETA_card_main.GHG_CardMain .ETA_card_center .Main_Section .ETA_card_Section .ETA_card_Section_head h5 {
            font-size: 20px;
            font-weight: 700;
            color: #293e63;
            margin: 0px;
          }
          .ETA_card_main.GHG_CardMain .ETA_card_center .Main_Section .ETA_card_Section .ETA_card_Section_sub h6 {
            color: #00b05f;
          }
          .report-company-logo {
            background-color: #fff;
            position: absolute;
            top: -34px;
            right: -96px;
            padding: 12px;
            border-radius: 5px 0px 0px 5px;
          }
          .Section_cover .ETA_card_Section {
            background-color: #f8fafc;
          }
          .Section_cover .Section_cover_head {
            position: relative;
            padding: 50px;
            color: #1e293b;
            z-index: 1;
          }
          .Section_cover .Section_cover_head h2 {
            font-size: 4rem;
            font-weight: 500;
            line-height: 1.2;
          }
          .Section_cover .Section_cover_head h3 {
            font-size: 2rem;
            font-weight: 500;
            line-height: 1.2;
          }
          ${WORD_DOCX_GHG_COMPAT_INLINE_CSS}
          ${ghgDocxTableP}
        </style>
      </head>
      <body>${embeddedBody}</body>
      </html>
    `;
    let str = normalizeHtmlClassAndPageBreaksForExport(htmlString);
    str = Utils.repairRupeeMojibake(str);
    const convertedDoc = htmlDocx.asBlob(str);
    const fileURL = window.URL.createObjectURL(convertedDoc);
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = fileName;
    link.click();
    if (typeof options.onComplete === 'function') {
      options.onComplete();
    }
    };

    prepareHtmlForDocxExport(bodyHtml, buildAndDownload, {
      ghgInnerImages: isGhgExport,
    });
  }

  static getESGHighlightsCardBasedHTML(result) {
    if (!result.AnalysisResult || result.AnalysisResult.length === 0) return '';

    const cardsHTML = result.AnalysisResult.map((item) => {
      // Determine color based on percentage value
      const percentage = item.Highlight_Value;
      const isPositive = percentage.includes('+') || (percentage.includes('%') && !percentage.includes('-'));

      // Ensure percentage has % symbol
      let displayValue = item.Highlight_Value;
      if (!displayValue.includes('%')) {
        displayValue = displayValue + '%';
      }

      return `
        <div style="flex: 0 0 auto; min-width: 280px; max-width: 350px; margin-bottom: 1rem;">
          <div style="border: 2px solid #e5e7eb; border-radius: 8px; background: white; padding: 1.5rem; min-height: 200px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
            <div style="text-align: center; display: flex; flex-direction: column; justify-content: space-between; height: 100%;">
              <div style="margin-bottom: 1rem;">
                <span style="font-size: 2.5rem; font-weight: bold; color: ${isPositive ? '#059669' : '#dc2626'}; display: inline-block;">${displayValue}</span>
              </div>
              <div style="flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
                <h6 style="font-size: 1rem; font-weight: bold; line-height: 1.4; color: #374151; margin-bottom: 1rem; white-space: normal; word-wrap: break-word;">${item.title}</h6>
                <p style="font-size: 0.875rem; line-height: 1.5; color: #6b7280; margin-bottom: 0; white-space: normal; word-wrap: break-word; overflow-wrap: break-word;">${item.Description}</p>
              </div>
            </div>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="container-fluid px-0" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div style="display: flex; flex-wrap: wrap; gap: 1rem; justify-content: flex-start;">
          ${cardsHTML}
        </div>
      </div>
    `;
  }

  static decodeErrorMessage(status, details, t) {
    switch (status) {
      case 401:
      case 429:
        return {
          header: t("decodeErrorMessage.ESGHeader"),
          message: t("decodeErrorMessage.ESGMessage")
        };
      case 404:
        return {
          header: t("decodeErrorMessage.ESGAIHeader"),
          message: t("decodeErrorMessage.ESGAIMessage")
        };
      case 403:
        return {
          header: 'Data not found',
          message: `Please add the '${ReportingUtils.errorFieldToString(details.trim())}' on the company details page and try again.`
        };
      case 500:
        return {
          header: t("decodeErrorMessage.ServerErrorHeader"),
          message: t("decodeErrorMessage.ServerErrorMessage")
        };
      default: break;
    }
    return {
      header: t("decodeErrorMessage.SomethingWentWrongHeader"),
      message: t("decodeErrorMessage.SomethingWentWrongMessage")
    };
  }

  static errorFieldToString(str) {
    // Extract the substring after "Missing field: "
    const prefix = "Missing field: ";
    const startIndex = str.indexOf(prefix) + prefix.length;
    const endIndex = str.indexOf('.', startIndex);
    const fieldName = str.slice(startIndex, endIndex).trim();

    // Split the field name by underscores
    let words = fieldName.split('_');
    // Capitalize the first letter of each word and lower case the rest
    words = words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    return words.join(' ');
  }

  static getESRSEnvironmentSection(e1Data, e2Data, e3Data, e4Data, e5Data, language, t, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "Environment.label", language),
      index: 6,
      sortOrder: 7,
      type: ReportSection.ENVIRONMENT,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.ENV_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif; padding: 20px; background-color: #fff; color: #333; line-height: 1.6;">
          <div
              style="position: relative;padding: 10px 0px;z-index: 1;color: #fff; margin: 0;border-top-left-radius: 50px;border-bottom-left-radius: 50px;">
              <div
                  style="content: '';position: absolute;left: 0;background-color: #2dc26b;right: 54%;height: 100%;top: 0;margin-left: -52px;z-index: 0;border-top-right-radius: 50px;border-bottom-right-radius: 50px;">
              </div>
              <h3 style="color: #fff;margin: 0; position: relative;z-index: 1;">
                  ${getTranslation(i18n, "Environment.label", language)}
              </h3>
          </div>
          <div style="padding: 20px;">
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "E1ClimateChange.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "E2Pollution.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "E3Waterandmarineresourse.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "E4Biodiversityandecosystems.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "E5Resourceuseandcirculareconomy.label", language)}</h2>
          </div>
      </div>`,
        },
        {
          name: getTranslation(i18n, "E1ClimateChange.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.E1,
          html: pageHtml(e1Data, normalizedLang, 'Environment'),
        },
        {
          name: getTranslation(i18n, "E2Pollution.label", language),
          index: 2,
          sortOrder: 3,
          type: ReportPageType.E2,
          html: pageHtml(e2Data, normalizedLang, 'Environment'),
        },
        {
          name: getTranslation(i18n, "E3Waterandmarineresourse.label", language),
          index: 3,
          sortOrder: 4,
          type: ReportPageType.E3,
          html: pageHtml(e3Data, normalizedLang, 'Environment'),
        },
        {
          name: getTranslation(i18n, "E4Biodiversityandecosystems.label", language),
          index: 4,
          sortOrder: 5,
          type: ReportPageType.E4,
          html: pageHtml(e4Data, normalizedLang, 'Environment'),
        },
        {
          name: getTranslation(i18n, "E5Resourceuseandcirculareconomy.label", language),
          index: 5,
          sortOrder: 6,
          type: ReportPageType.E5,
          html: pageHtml(e5Data, normalizedLang, 'Environment'),
        },
      ]
    }
  }

  static getESRSSocialSection(s1Data, s2Data, s3Data, s4Data, language, t, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "Social.label", language),
      index: 7,
      sortOrder: 8,
      type: ReportSection.SOCIAL,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.SOCIAL_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif; padding: 20px; background-color: #fff; color: #333; line-height: 1.6;">
          <div
              style="position: relative;padding: 10px 0px;z-index: 1;color: #fff; margin: 0;border-top-left-radius: 50px;border-bottom-left-radius: 50px;">
              <div
                  style="content: '';position: absolute;left: 0;background-color: #007bff;right: 54%;height: 100%;top: 0;margin-left: -52px;z-index: 0;border-top-right-radius: 50px;border-bottom-right-radius: 50px;">
              </div>
              <h3 style="color: #fff;margin: 0; position: relative;z-index: 1;">
                  ${getTranslation(i18n, "Social.label", language)}
              </h3>
          </div>
          <div style="padding: 20px;">
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "S1Ownworkforce.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "S2Workersinthevaluechain.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "S3Affectedcommunities.label", language)}</h2>
              <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "S4Consumersandendusers.label", language)}</h2>
          </div>
      </div>`,
        },
        {
          name: getTranslation(i18n, "S1Ownworkforce.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.S1,
          html: pageHtml(s1Data, normalizedLang, 'Social'),
        },
        {
          name: getTranslation(i18n, "S2Workersinthevaluechain.label", language),
          index: 2,
          sortOrder: 3,
          type: ReportPageType.S2,
          html: pageHtml(s2Data, normalizedLang, 'Social')
        },
        {
          name: getTranslation(i18n, "S3Affectedcommunities.label", language),
          index: 3,
          sortOrder: 4,
          type: ReportPageType.S3,
          html: pageHtml(s3Data, normalizedLang, 'Social'),
        },
        {
          name: getTranslation(i18n, "S4Consumersandendusers.label", language),
          index: 4,
          sortOrder: 5,
          type: ReportPageType.S4,
          html: pageHtml(s4Data, normalizedLang, 'Social'),
        },
      ]
    }
  }


  static getESRSGovernanceSection(g1Data, language, t, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    return {
      name: getTranslation(i18n, "Governance.label", language),
      index: 8,
      sortOrder: 9,
      type: ReportSection.GOVERNANCE,
      pages: [
        {
          name: getTranslation(i18n, "Overview.label", language),
          index: 0,
          sortOrder: 1,
          type: ReportPageType.GOV_OVERVIEW,
          html: `<div style="font-family: Arial, sans-serif; padding: 20px; background-color: #fff; color: #333; line-height: 1.6;">
          <div
              style="position: relative;padding: 10px 0px;z-index: 1;color: #fff; margin: 0;border-top-left-radius: 50px;border-bottom-left-radius: 50px;">
              <div
                  style="content: '';position: absolute;left: 0;background-color: #f7c300;right: 54%;height: 100%;top: 0;margin-left: -52px;z-index: 0;border-top-right-radius: 50px;border-bottom-right-radius: 50px;">
              </div>
              <h3 style="color: #fff;margin: 0; position: relative;z-index: 1;">
                  ${getTranslation(i18n, "Governance.label", language)}
              </h3>
          </div>
          <div style="padding: 20px;">
          <h2 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "G1Businessconduct.label", language)}</h2>
      </div>
      </div>`,
        },
        {
          name: getTranslation(i18n, "G1Businessconduct.label", language),
          index: 1,
          sortOrder: 2,
          type: ReportPageType.G1,
          html: pageHtml(g1Data, normalizedLang, 'Governance'),
        }]
    }
  }

  static getESRSCoverSection(reportClone, companyId, language, t, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
      "<companyId>",
      companyId
    );
    const year = reportClone?.periodStart ? reportClone.periodStart.split('-')[0] : '2024';
    return {
      name: getTranslation(i18n, "FrontCoverpage.label", language),
      index: 0,
      sortOrder: 1,
      type: ReportSection.TITLE,
      pages: [{
        name: getTranslation(i18n, "FrontCoverpage.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.TITLE,
        html: `
          <div>
                <div style="position: relative; overflow: hidden; background-color: #fff; page-break-after: always; height: calc(100vh - 107px);">
                  <div style="overflow: hidden; height: 100%; width: 50%; position: relative;">
                    <div style="content: ''; background-color: rgba(23, 96, 166, 0.78); position: absolute; left: 0; width: 60px; top: 0; bottom: 0;">
                    </div>
                  <div style="content: ''; background-color: rgba(242, 183, 20, 0.78); position: absolute; left: 60px; width: 60px; top: 0; bottom: 0;">
                </div>
                <img src='${process.env.REACT_APP_REPORT_SERVICE_BASE_URL}/esrs/images/Cover-image-1-1280x720.jpg' alt="Cover" style="max-width: fit-content; height: 100%;" />
              </div>
              <div style="position: absolute; top: 0; right: 0; bottom: 0; left: 24rem; padding: 6rem 1rem; display: flex; align-items: center;"><div style="position: absolute; top: 40px; right: 40px; width: 140px;">
              <img src=${logoURL} alt="Company Logo" style="width: 100%;" /></div>
              <div><h2 style="font-size: 72px; font-weight: 800; color: #0a58ca; margin-bottom: 0;">${getTranslation(i18n, "FY.label", language)} ${year}</h2><h3 style="font-size: 44px; font-weight: 700; color: #091a2a;">${getTranslation(i18n, "EuropeanSustainability.label", language)} <br /> ${getTranslation(i18n, "ReportingStandards.label", language)} <br /> (${getTranslation(i18n, "ESRS.label", language)})</h3></div>
              <div style="position: absolute; bottom: 40px; right: 40px;"></div>
              </div>
              </div>
          </div>
        `,
      }]
    }
  }

  static getESRSTableOfContentsSection(t, language, i18n) {
    const L = (key) => escapeTocHtml(getTranslation(i18n, key, language));
    const imgBase = `${process.env.REACT_APP_REPORT_SERVICE_BASE_URL}/esrs/images`;

    const tocHtml = buildIconGridTocHtml(L('TableofContents.label'), [
      {
        bg: '#0a58ca',
        iconSrc: `${imgBase}/introduction_icon.png`,
        title: L('Introduction.label'),
        items: [L('Leadershipmessages.label'), L('AbouttheReport.label'), L('AbouttheCompany.label'), L('OurESGApproach.label')],
      },
      {
        bg: '#2dc26b',
        iconSrc: `${imgBase}/environment_icon.png`,
        title: L('Environment.label'),
        items: [
          L('E1ClimateChange.label'),
          L('E2Pollution.label'),
          L('E3Waterandmarineresourse.label'),
          L('E4Biodiversityandecosystems.label'),
          L('E5Resourceuseandcirculareconomy.label'),
        ],
      },
      {
        bg: '#007bff',
        iconSrc: `${imgBase}/social_icon.png`,
        title: L('Social.label'),
        items: [
          L('S1Ownworkforce.label'),
          L('S2Workersinthevaluechain.label'),
          L('S3Affectedcommunities.label'),
          L('S4Consumersandendusers.label'),
        ],
      },
      {
        bg: '#e2b80f',
        iconSrc: `${imgBase}/governance_icon.png`,
        title: L('Governance.label'),
        items: [L('G1Businessconduct.label')],
      },
    ]);
    
    return {
      name: getTranslation(i18n, "TableofContents.label", language),
      index: 1,
      sortOrder: 2,
      type: ReportSection.CONTENTS,
      pages: [{
        name: getTranslation(i18n, "TableofContents.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.CONTENTS,
        html: tocHtml,
      }]
    };
  }

  static getESRSBackCover(companyId, language, t, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    
    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
      "<companyId>",
      companyId
    );
    return {
      name: getTranslation(i18n, "Root.label", language),
      index: 10,
      sortOrder: 11,
      type: ReportSection.ROOT,
      pages: [
        {
          name: getTranslation(i18n, "BackCover.label", language),
          sortOrder: 0,
          index: 1,
          type: ReportPageType.ROOT,
          html: `<div style="position: relative; overflow: hidden; background-color: #fff; page-break-after: always; height: calc(100vh - 107px);">
          <div style="overflow: hidden; height: 100%; width: 50%; position: relative; left: 50%; overflow: hidden;">
            <div style="content: ''; background-color: rgba(23, 96, 166, 0.78); position: absolute; left: 0px; width: 60px; top: 0px; bottom: 0px;"></div>
            <div style="content: ''; background-color: rgba(242, 183, 20, 0.78); position: absolute; left: 60px; width: 60px; top: 0px; bottom: 0px;"></div>
            <img src='${process.env.REACT_APP_REPORT_SERVICE_BASE_URL}/esrs/images/Cover-image-1-1280x720.jpg' alt="Cover" style="max-width: fit-content; height: 100%;" />
          </div>
          <div style="position: absolute; top: 0px; left: 0px; bottom: 0px; z-index: 1; right: 37rem; padding: 6rem 5rem; display: flex; align-items: center;">
            <div style="position: absolute; top: 40px; left: 40px; width: 140px;">
              <img src=${logoURL} alt="Company Logo" style="width: 100%;" />
            </div>
            <div>
              <h2 style="font-size: 72px; font-weight: 800; color: #0a58ca; margin-bottom: 0px;">${getTranslation(i18n, "FY.label", language)} 2024</h2>
              <h3 style="font-size: 44px; font-weight: 700; color: #091a2a;">${getTranslation(i18n, "EuropeanSustainability.label", language)} <br /> ${getTranslation(i18n, "ReportingStandards.label", language)} <br /> (${getTranslation(i18n, "ESRS.label", language)})</h3>
            </div>
            <div style="position: absolute; bottom: 40px; left: 40px;">
            </div>
          </div>
        </div>`,
        },
      ]
    };
  }

  static getSustContentIndexPageDefaultHTML() {
    return `
      <div style="font-family: Arial, sans-serif;font-weight:400;">
        <div>
          <h4>Content Index</h4>
          <p>&nbsp;</p>
        </div>
      </div>
    `;
  }

  static formatPeriodForAIPayload = (periodStart, periodEnd) => {
    // Handle null or undefined cases
    if (!periodStart || !periodEnd) {
      return "";
    }

    // Format the dates using Moment.js
    const startString = moment(periodStart).format('MMMMYYYY');
    const endString = moment(periodEnd).format('MMMMYYYY');

    // Combine the formatted strings
    return `${startString}-${endString}`;
  };


  /**
 * Maps esgSummaryDataDTO response to AI payload structure
 */
  static mapESGSummaryDataToAIPayload(esgSummaryData, config = {}) {
    const aiPayload = {
      job_id: null,
      company: null,
      sections: [],
      style_id: [],
      tone_id: [],
      language: null,
      is_return: false
    };

    if (!esgSummaryData || !esgSummaryData.topics) {
      return aiPayload;
    }

    // Group topics by category (Environmental, Social, Governance)
    const topicsByCategory = ReportingUtils.groupTopicsByCategory(esgSummaryData.topics);

    // Create sections for each ESG category
    Object.entries(topicsByCategory).forEach(([categoryName, topics]) => {
      const section = ReportingUtils.createSection(categoryName, topics, esgSummaryData, config);
      if (section) {
        aiPayload.sections.push(section);
      }
    });

    return aiPayload;
  }

  /**
   * Groups topics by their category
   */
  static groupTopicsByCategory(topics) {
    const categories = {
      'Environment': [],
      'Social': [],
      'Governance': []
    };

    topics.forEach(topic => {
      if (topic.categoryType === 'ENVIRONMENTAL') {
        categories.Environment.push(topic);
      } else if (topic.categoryType === 'SOCIAL') {
        categories.Social.push(topic);
      } else if (topic.categoryType === 'GOVERNANCE') {
        categories.Governance.push(topic);
      }
    });

    return categories;
  }

  /**
   * Creates a section for a specific category
   */
  static createSection(categoryName, topics, esgSummaryData, config) {
    if (!topics || topics.length === 0) {
      return null;
    }

    const section = {
      section: categoryName,
      sectionId: null,
      sectionType: 0,
      pageId: null,
      pageType: 0,
      word_limit: config.wordLimit || "auto",
      metrics: [],
      subsections: [],
      supplementary_data: []
    };

    // Create subsections and supplementary data for each topic
    topics.forEach(topic => {
      // Create subsection
      const subsection = {
        sub_section_name: topic.name,
        sectionId: null,
        sectionType: 0,
        pageId: null,
        pageType: 0,
        word_limit: config.wordLimit || "auto"
      };
      section.subsections.push(subsection);

      // Create supplementary data
      const supplementaryData = {
        topic: topic.name,
        qualitative_metrics: [],
        quantitative_metrics: [],
        case_studies: []
      };

      // Map quantitative data - using topic.id as string key
      if (esgSummaryData.quantitativeData && esgSummaryData.quantitativeData[topic.id.toString()]) {
        supplementaryData.quantitative_metrics = ReportingUtils.mapQuantitativeData(
          esgSummaryData.quantitativeData[topic.id.toString()]
        );
      }

      // Map qualitative data
      if (esgSummaryData.qualitativeData) {
        supplementaryData.qualitative_metrics = ReportingUtils.mapQualitativeData(
          esgSummaryData.qualitativeData,
          topic.id
        );
      }

      // Map case studies (content blocks)
      if (esgSummaryData.contentBlocks) {
        supplementaryData.case_studies = ReportingUtils.mapCaseStudies(
          esgSummaryData.contentBlocks,
          topic.id
        );
      }

      section.supplementary_data.push(supplementaryData);
    });

    return section;
  }

  /**
   * Maps quantitative data from the actual ESGBulkDataDTO structure
   */
  static mapQuantitativeData(topicData) {
    const quantitativeMetrics = [];

    if (!topicData || !topicData.metrics) {
      return quantitativeMetrics;
    }

    topicData.metrics.forEach(metric => {
      const metricData = {
        metric: metric.metric_name || "NA",
        values: [],
        datapoints: []
      };

      // Map metric period_data to values
      if (metric.period_data && metric.period_data.length > 0) {
        metric.period_data.forEach(periodData => {
          const metricValue = {
            year: ReportingUtils.formatPeriodForAIPayload(periodData.period_start, periodData.period_end),
            organisation_total: periodData.total_value ? periodData.total_value.toString() + (periodData.unit_of_measure ? " " + periodData.unit_of_measure : "") : "",
            entities: []
          };

          // Map branch data if available
          if (periodData.branch_data && periodData.branch_data.length > 0) {
            periodData.branch_data.forEach(branch => {
              metricValue.entities.push({
                branch: branch.branch_name || "",
                branch_value: branch.value ? branch.value.toString() + (periodData.unit_of_measure ? " " + periodData.unit_of_measure : "") : ""
              });
            });
          }

          metricData.values.push(metricValue);
        });
      }

      // Map datapoints
      if (metric.datapoints && metric.datapoints.length > 0) {
        metric.datapoints.forEach(datapoint => {
          const datapointData = {
            datapoint: datapoint.datapoint_name || "",
            values: []
          };

          if (datapoint.period_data && datapoint.period_data.length > 0) {
            datapoint.period_data.forEach(periodData => {
              const datapointValue = {
                year: ReportingUtils.formatPeriodForAIPayload(periodData.period_start, periodData.period_end),
                organisation_total: periodData.total_value ? periodData.total_value.toString() + (periodData.unit_of_measure ? " " + periodData.unit_of_measure : "") : "",
                entities: []
              };

              // Map branch data for datapoints
              if (periodData.branch_data && periodData.branch_data.length > 0) {
                periodData.branch_data.forEach(branch => {
                  datapointValue.entities.push({
                    branch: branch.branch_name || "",
                    branch_value: branch.value ? branch.value.toString() + (periodData.unit_of_measure ? " " + periodData.unit_of_measure : "") : ""
                  });
                });
              }

              datapointData.values.push(datapointValue);
            });
          }

          // Only add datapoint if it has values
          if (datapointData.values.length > 0) {
            metricData.datapoints.push(datapointData);
          }
        });
      }

      // Only add metric if it has values or datapoints
      if (metricData.values.length > 0 || metricData.datapoints.length > 0) {
        quantitativeMetrics.push(metricData);
      }
    });

    return quantitativeMetrics;
  }

  /**
   * Maps qualitative data from the actual ESGBulkDataDTO structure
   */
  static mapQualitativeData(qualitativeData, topicId) {
    const qualitativeMetrics = [];

    if (!qualitativeData) {
      return qualitativeMetrics;
    }

    // Find qualitative data for the specific topic
    const topicQualitativeData = qualitativeData.find(data => data.topicId === topicId);
    if (!topicQualitativeData || !topicQualitativeData.metrics) {
      return qualitativeMetrics;
    }

    topicQualitativeData.metrics.forEach(metric => {
      const metricData = {
        metric: metric.metricName || "NA",
        questions: []
      };

      if (metric.questions && metric.questions.length > 0) {
        metric.questions.forEach(question => {
          const questionData = {
            question: question.questionText || "",
            answers: []
          };

          // Group main question answers by year
          if (question.answers && question.answers.length > 0) {
            const answersByYear = ReportingUtils.groupAnswersByYear(question.answers);
            Object.entries(answersByYear).forEach(([year, answers]) => {
              const answerData = {
                year: parseInt(year),
                answer: answers.join(" "), // Concatenate multiple answers for the same year
                branchAnswers: []
              };

              // Map branch consolidated data for this year
              if (question.branchConsolidated && question.branchConsolidated.length > 0) {
                question.branchConsolidated.forEach(branch => {
                  if (branch.answers && branch.answers.length > 0) {
                    const branchAnswersByYear = ReportingUtils.groupAnswersByYear(branch.answers);
                    if (branchAnswersByYear[year]) {
                      answerData.branchAnswers.push({
                        branch: branch.branchName || "",
                        answer: branchAnswersByYear[year].join(" ")
                      });
                    }
                  }
                });
              }

              questionData.answers.push(answerData);
            });
          }

          // Process sub-questions if they exist
          if (question.subQuestions && question.subQuestions.length > 0) {
            question.subQuestions.forEach(subQuestion => {
              const subQuestionData = {
                question: subQuestion.questionText || "",
                answers: []
              };

              // Group sub-question answers by year
              if (subQuestion.answers && subQuestion.answers.length > 0) {
                const answersByYear = ReportingUtils.groupAnswersByYear(subQuestion.answers);
                Object.entries(answersByYear).forEach(([year, answers]) => {
                  const answerData = {
                    year: parseInt(year),
                    answer: answers.join(" "), // Concatenate multiple answers for the same year
                    branchAnswers: []
                  };

                  // Map branch consolidated data for sub-questions
                  if (subQuestion.branchConsolidated && subQuestion.branchConsolidated.length > 0) {
                    subQuestion.branchConsolidated.forEach(branch => {
                      if (branch.answers && branch.answers.length > 0) {
                        const branchAnswersByYear = ReportingUtils.groupAnswersByYear(branch.answers);
                        if (branchAnswersByYear[year]) {
                          answerData.branchAnswers.push({
                            branch: branch.branchName || "",
                            answer: branchAnswersByYear[year].join(" ")
                          });
                        }
                      }
                    });
                  }

                  subQuestionData.answers.push(answerData);
                });
              }

              metricData.questions.push(subQuestionData);
            });
          }

          metricData.questions.push(questionData);
        });
      }

      qualitativeMetrics.push(metricData);
    });

    return qualitativeMetrics;
  }

  /**
   * Groups answers by year and concatenates multiple answers for the same year
   */
  static groupAnswersByYear(answers) {
    const groupedAnswers = {};

    answers.forEach(answer => {
      if (answer.period && answer.answer) {
        const year = answer.period.substring(0, 4);
        if (!groupedAnswers[year]) {
          groupedAnswers[year] = [];
        }
        groupedAnswers[year].push(answer.answer);
      }
    });

    return groupedAnswers;
  }

  /**
   * Maps content blocks to case studies
   */
  static mapCaseStudies(contentBlocks, topicId) {
    const caseStudies = [];

    if (!contentBlocks) {
      return caseStudies;
    }

    // Filter content blocks for the specific topic
    const topicContentBlocks = contentBlocks.filter(block =>
      block.topic && block.topic.id === topicId
    );

    topicContentBlocks.forEach(block => {
      const caseStudy = {
        Title: block.name || "",
        Topics: block.topic ? block.topic.name : "",
        About: block.about || "",
        Description: block.description || "",
        year: block.period ? parseInt(block.period.substring(0, 4)) : new Date().getFullYear()
      };

      caseStudies.push(caseStudy);
    });

    return caseStudies;
  }

  /**
   * Creates and configures a hidden iframe for printing
   * @param {string} fileName - The filename for identification
   * @returns {HTMLIFrameElement|null} The configured iframe or null if creation fails
   */
  static createPrintIframe = (fileName) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    iframe.setAttribute('title', 'Print Frame');
    iframe.setAttribute('name', fileName);
    
    try {
      document.body.appendChild(iframe);
      return iframe;
    } catch (error) {
      console.error('Error appending iframe to body:', error);
      return null;
    }
  };

  /**
   * Generates the complete HTML document string with embedded styles for printing
   * @param {string} htmlContent - The HTML content to print
   * @param {string} documentTitle - The document title
   * @returns {string} The complete HTML document string
   */
  static generatePrintHTML = (htmlContent, documentTitle) => {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${documentTitle}</title>
        <style type="text/css">
          /* Base styles */
          body {
            padding: 0px;
            margin: 0px;
            font-size: 13px;
            color: #424242;
            font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
            background: white;
          }
          .none { list-style: none; }
          .fw-bold { font-weight: bold; }
          b, strong {
            font-weight: bolder;
          }
          ${BRSR_EXPORT_TABLE_AND_SPACING_CSS}
          .ETA_card_Section_head h5 {
            font-size: 20px;
            font-weight: 700;
            color: #0a58ca;
          }
          .ETA_card_Section_sub_head {
            margin-bottom: 5px;
            padding-left: 27px;
            position: relative;
            font-size: 15px;
            color: #0a58ca;
          }
          .ETA_card_Section_sub_head .count {
            position: absolute;
            top: 0px;
            left: 0px;
            width: 24px;
            display: block;
            text-align: right;
            margin-right: 5px;
            font-weight: bolder;
            color: #0a58ca;
          }
          th.FY_class {
            background-color: #0d6efd38 !important;
          }
          th.FY_class_sub {
            background-color: #0d6efd1f !important;
          }
          td.FY_class_content {
            background-color: #0d6efd14 !important;
          }
          .count {
            font-weight: bold;
          }
          .brsr-list-item {
            margin: 2px 2px 2px 15px;
            margin-bottom: 10px;
          }
          .brsr-sublist {
            margin-left: 15px;
          }
          .mt-2 {
            margin-top: 7.5px;
          }
          .mb-4 {
            margin-bottom: 7.5px;
          }
          .ghg-dark-highlight {
            background-color: #00b05f;
            color: white;
          }
          .ETA_card_main.GHG_CardMain .ETA_card_center .Main_Section .ETA_card_Section .ETA_card_Section_head h5 {
            font-size: 20px;
            font-weight: 700;
            color: #293e63;
            margin: 0px;
          }
          .ETA_card_main.GHG_CardMain .ETA_card_center .Main_Section .ETA_card_Section .ETA_card_Section_sub h6 {
            color: #00b05f;
          }
          .report-company-logo {
            background-color: #fff;
            position: absolute;
            top: -34px;
            right: -96px;
            padding: 12px;
            border-radius: 5px 0px 0px 5px;
          }
          .front-page {
            margin-top: 40px !important;
            margin-bottom: 40px !important;
          }
          .GHG_CardMain > .ETA_card_center > .Main_Section > .ETA_card_Section table > tbody > tr {
            border-top: none !important;
          }

          .Section_cover .ETA_card_Section {
            background-color: #f8fafc;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .Section_cover .Section_cover_head {
            position: relative;
            padding: 50px;
            color: #1e293b;
            z-index: 1;
          }
          .Section_cover .Section_cover_head h2 {
            font-size: 4rem;
            font-weight: 500;
            line-height: 1.2;
          }
          .Section_cover .Section_cover_head h3 {
            font-size: 2rem;
            font-weight: 500;
            line-height: 1.2;
          }

          /* BRSR title/cover: one full page in print preview and Save as PDF */
          .brsr-report-cover-page {
            page-break-after: always;
            page-break-inside: avoid;
            width: 100%;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
          }
          .brsr-report-cover-page > .ETA_card_center {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            min-height: 100%;
            width: 100%;
          }
          .brsr-report-cover-page .Main_Section.Section_cover {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            min-height: 100%;
          }
          .brsr-report-cover-page .Section_cover .ETA_card_Section {
            flex: 1 1 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100%;
            width: 100%;
          }
          .brsr-report-cover-page .front-page {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: 100%;
            width: 100%;
          }
          .brsr-report-cover-page .Section_cover_head {
            width: 100%;
          }

          /* Print-specific styles */
          @media print {
            @page {
              size: A4;
              margin: 10mm;
              /* Disable headers and footers by default */
              /* Note: This is a CSS hint, but browser settings may override */
            }
            /* Cover: bleed into body padding so one full printable page; other pages keep body padding */
            .brsr-report-cover-page {
              margin: -20px -20px 0 -20px;
              width: calc(100% + 40px);
              box-sizing: border-box;
              min-height: calc(277mm - 40px);
            }
            body {
              margin: 0;
              padding: 20px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            /* Ensure page breaks work properly */
            .page-break {
              page-break-before: always;
            }
            .no-break {
              page-break-inside: avoid;
            }
            /* Tables: allow breaks on large grids; keep rows intact; repeat thead on each page */
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            thead {
              display: table-header-group;
            }
            th,
            td {
              vertical-align: top;
            }
            tfoot {
              display: table-footer-group;
            }
            /* Ensure images print correctly */
            img {
              max-width: 100%;
              height: auto;
            }
            /* Preserve background colors in print */
            th.FY_class,
            th.FY_class_sub,
            td.FY_class_content {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .ghg-dark-highlight {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            /* BRSR Section B (NGRBC / principle grids): fit every column on PDF — no horizontal clip */
            .brsr-section-b-grid .table-responsive {
              overflow: visible !important;
              width: 100% !important;
              max-width: none !important;
            }
            .brsr-section-b-grid .ETA_card_center,
            .brsr-section-b-grid .ETA_card_Section_sub_body,
            .brsr-section-b-grid .ETA_card_Section_sub {
              overflow: visible !important;
              max-width: none !important;
            }
            .brsr-section-b-grid table.table,
            .brsr-section-b-grid table.table-bordered {
              max-width: none !important;
              width: 100% !important;
              table-layout: fixed !important;
              font-size: 7.5pt;
            }
            .brsr-section-b-grid th,
            .brsr-section-b-grid td {
              word-break: break-word;
              overflow-wrap: anywhere;
              padding: 3px 5px !important;
            }
            .brsr-section-b-grid .brsr-section-b-grid__disclosure-col,
            .brsr-section-b-grid .brsr-section-b-grid__subject-col {
              width: 26% !important;
              min-width: 0 !important;
            }
            .brsr-section-b-grid__q12-block .brsr-section-b-grid__nested-table tbody > tr > td:first-of-type {
              width: 26% !important;
              min-width: 0 !important;
            }
            .brsr-section-b-grid .brsr-section-b-grid__group-header {
              font-size: 6.75pt !important;
              line-height: 1.25 !important;
            }
          }

          /* Screen-only styles (hidden when printing) */
          @media screen {
            body {
              padding: 20px;
            }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `;
  };

  /**
   * Processes HTML string by replacing className attributes with inline styles
   * @param {string} htmlString - The HTML string to process
   * @returns {string} The processed HTML string
   */
  static processHTMLForPrint = (htmlString) => {
    return normalizeHtmlClassAndPageBreaksForExport(htmlString);
  };

  /**
   * Sets the document title in multiple ways for better browser compatibility
   * Some browsers use document.title for the PDF filename
   * @param {Window} iframeWindow - The iframe's content window
   * @param {string} documentTitle - The title to set
   */
  static setupDocumentTitle = (iframeWindow, documentTitle) => {
    if (!iframeWindow || !iframeWindow.document) return;

    // Set document title
    iframeWindow.document.title = documentTitle;
    
    // Set title element directly
    const titleElement = iframeWindow.document.querySelector('title');
    if (titleElement) {
      titleElement.textContent = documentTitle;
    } else {
      // Create title element if it doesn't exist
      const head = iframeWindow.document.head || iframeWindow.document.getElementsByTagName('head')[0];
      if (head) {
        const newTitle = iframeWindow.document.createElement('title');
        newTitle.textContent = documentTitle;
        head.appendChild(newTitle);
      }
    }
    
    // Set window name (some browsers might use this)
    try {
      iframeWindow.name = documentTitle;
    } catch (e) {
      // Some browsers don't allow setting window.name
    }
  };

  /**
   * Downloads BRSR Report as PDF using browser's native print-to-PDF functionality
   * Uses a hidden iframe to avoid opening a new tab/window
   * 
   * Note: The "Show headers and page numbers" checkbox in the print dialog is a browser
   * preference that cannot be controlled programmatically for security reasons. Users
   * will need to uncheck this option manually in the print dialog if they don't want
   * headers/footers. The iframe will automatically close after the print dialog is dismissed.
   * 
   * @param {string} htmlContent - The HTML content to print
   * @param {string} fileName - The filename for the PDF (used as document title)
   */
  static downloadBRSRReportPDFViaPrint = (htmlContent, fileName) => {
    // Validate inputs
    if (!htmlContent) {
      console.error('downloadBRSRReportPDFViaPrint: htmlContent is required');
      return;
    }

    if (!fileName) {
      console.error('downloadBRSRReportPDFViaPrint: fileName is required');
      return;
    }

    // Create and configure iframe
    const iframe = ReportingUtils.createPrintIframe(fileName);
    if (!iframe) {
      return;
    }

    const documentTitle = fileName;

    // Generate HTML with embedded styles
    const htmlString = ReportingUtils.generatePrintHTML(htmlContent, documentTitle);
    
    // Process HTML for print compatibility
    const processedHtml = ReportingUtils.processHTMLForPrint(htmlString);

    // Track if cleanup has been called to prevent multiple cleanups
    let cleanupCalled = false;
    let printTriggered = false;

    // Function to clean up iframe after printing
    const cleanup = () => {
      if (cleanupCalled) return;
      cleanupCalled = true;

      // Use requestAnimationFrame for smoother cleanup
      requestAnimationFrame(() => {
        try {
          if (iframe && iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        } catch (error) {
          console.error('Error removing iframe:', error);
        }
      });
    };

    // Function to trigger print dialog
    const triggerPrint = () => {
      if (printTriggered) return;
      printTriggered = true;

      try {
        const iframeWindow = iframe.contentWindow;
        if (iframeWindow && iframeWindow.document) {
          // Set document title for PDF filename
          ReportingUtils.setupDocumentTitle(iframeWindow, documentTitle);
          
          // Force a small delay to ensure title is set before print dialog opens
          setTimeout(() => {
            try {
              // Ensure title is still set right before print
              ReportingUtils.setupDocumentTitle(iframeWindow, documentTitle);
              
              // Focus the iframe window
              iframeWindow.focus();
              
              // Trigger print dialog
              iframeWindow.print();
            } catch (error) {
              console.error('Error triggering print:', error);
              cleanup();
            }
          }, 50);
        }
      } catch (error) {
        console.error('Error triggering print:', error);
        cleanup();
      }
    };

    // Write content to iframe and trigger print
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        console.error('Cannot access iframe document');
        cleanup();
        return;
      }

      iframeDoc.open();
      iframeDoc.write(processedHtml);
      iframeDoc.close();

      // Set up event listeners for print dialog
      const iframeWindow = iframe.contentWindow;
      if (iframeWindow) {
        // Listen for afterprint event to cleanup when dialog closes
        iframeWindow.addEventListener('afterprint', () => {
          cleanup();
        });

        // Also listen on the iframe element itself (for some browsers)
        iframe.addEventListener('load', () => {
          // Ensure document title is set after load
          ReportingUtils.setupDocumentTitle(iframeWindow, documentTitle);
        });
      }

      // Wait for iframe content to load, then trigger print dialog
      iframe.onload = () => {
        // Small delay to ensure all content is rendered (especially images)
        setTimeout(() => {
          triggerPrint();
        }, 250);
      };

      // Fallback: if onload doesn't fire, try printing after a short delay
      setTimeout(() => {
        try {
          const iframeWindow = iframe.contentWindow;
          if (iframeWindow && iframeWindow.document && iframeWindow.document.readyState === 'complete') {
            if (!printTriggered) {
              triggerPrint();
            }
          }
        } catch (error) {
          console.error('Error in print fallback:', error);
          if (!cleanupCalled) {
            cleanup();
          }
        }
      }, 1000);

      // Safety cleanup: if print dialog hasn't been triggered after 5 seconds, cleanup
      setTimeout(() => {
        if (!printTriggered && !cleanupCalled) {
          console.warn('Print dialog not triggered, cleaning up iframe');
          cleanup();
        }
      }, 5000);

    } catch (error) {
      console.error('Error setting up print iframe:', error);
      cleanup();
    }
  }
}
